import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend, LabelList
} from "recharts";
import { COUNTRIES } from "./countries";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const BILLING_TYPES = {
  A: { name: "Sin referencias", requires_oc: false, requires_hes: false, requires_ndp: false },
  B: { name: "Con OC", requires_oc: true, requires_hes: false, requires_ndp: false },
  C: { name: "Con OC + HES", requires_oc: true, requires_hes: true, requires_ndp: false },
  D: { name: "Con OC + Nota de Pedido", requires_oc: true, requires_hes: false, requires_ndp: true },
};
const PAYMENT_OPTIONS = [30, 45, 60];
const VERTICALS = ["Salud", "Educación", "Productivo", "Activo inmobiliario", "Prestador"];
const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const STATUS_OPTIONS = [
  { value: "active", label: "Activo", color: "#10b981", bg: "#d1fae5" },
  { value: "paused", label: "Pausado", color: "#f59e0b", bg: "#fef3c7" },
  { value: "inactive", label: "Baja", color: "#ef4444", bg: "#fee2e2" },
];

async function api(path, method = "GET", body = null) {
  const token = sessionStorage.getItem("egenya_token");
  const opts = { method, headers: { "Content-Type": "application/json" } };
  if (token) opts.headers["Authorization"] = `Bearer ${token}`;
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${API_URL}${path}`, opts);
  if (res.status === 401 && path !== "/config") {
    sessionStorage.removeItem("egenya_token");
    sessionStorage.removeItem("egenya_user");
    window.location.reload();
    return { success: false, error: "Sesión expirada" };
  }
  return res.json();
}
async function uploadPDF(file, clientId, docType, yearMonth) {
  const res = await api("/upload-url", "POST", { client_id: clientId, doc_type: docType, year_month: yearMonth || "", filename: file.name });
  if (!res.success) throw new Error(res.error);
  await fetch(res.upload_url, { method: "PUT", body: file, headers: { "Content-Type": "application/pdf" } });
  return res.file_key;
}
async function downloadPDF(fileKey) {
  const res = await api("/download-url", "POST", { file_key: fileKey });
  if (res.success) window.open(res.download_url, "_blank");
}

// Dispara la descarga de un archivo (la URL firmada de S3 ya viene con
// Content-Disposition: attachment) sin abrir una pestaña en blanco.
function downloadFromUrl(url) {
  const a = document.createElement("a");
  a.href = url;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (data.success) {
        sessionStorage.setItem("egenya_token", data.token);
        sessionStorage.setItem("egenya_user", JSON.stringify(data.user));
        onLogin(data.user);
      } else {
        setError(data.error || "Credenciales incorrectas");
      }
    } catch (err) {
      setError("Error de conexión");
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#1a1d29", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono&display=swap" rel="stylesheet" />
      <div style={{ background: "#fff", borderRadius: 16, padding: 40, width: 380, boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: "#1a1d29", margin: 0 }}>⚡ eGenya</h1>
          <p style={{ color: "#6b7280", fontSize: 13, margin: "6px 0 0" }}>Sistema de Facturación</p>
        </div>
        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Correo</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@egenya.cl" required
              style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Contraseña</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required
              style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
          </div>
          {error && <p style={{ color: "#ef4444", fontSize: 13, margin: "0 0 12px", textAlign: "center" }}>{error}</p>}
          <button type="submit" disabled={loading}
            style={{ width: "100%", padding: "12px", background: "#6366f1", color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: loading ? 0.6 : 1 }}>
            {loading ? "Ingresando..." : "Ingresar"}
          </button>
        </form>
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(() => {
    const saved = sessionStorage.getItem("egenya_user");
    return saved ? JSON.parse(saved) : null;
  });
  const [page, setPage] = useState("dashboard");
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [toast, setToast] = useState(null);
  const [ufValue, setUfValue] = useState(null);
  const [envInfo, setEnvInfo] = useState(null);
  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };
  const loadClients = useCallback(async () => { try { const res = await api("/clients"); if (res.success) setClients(res.data || []); } catch (e) { showToast("Error cargando clientes", "error"); } }, []);
  const loadInvoices = useCallback(async () => { try { const res = await api("/invoices"); if (res.success) setInvoices(res.data || []); } catch (e) { console.error(e); } }, []);
  const loadUF = useCallback(async () => { try { const res = await api("/uf"); if (res.success) setUfValue(res.uf_value); } catch (e) { console.error(e); } }, []);
  useEffect(() => { if (user) { loadClients(); loadInvoices(); loadUF(); api("/config").then(r => setEnvInfo(r)); } }, [user, loadClients, loadInvoices, loadUF]);
  const navTo = (pg, client = null) => { setPage(pg); setSelectedClient(client); };
  const logout = () => { sessionStorage.removeItem("egenya_token"); sessionStorage.removeItem("egenya_user"); setUser(null); };

  if (!user) return <LoginScreen onLogin={setUser} />;

  return (
    <div style={{ minHeight: "100vh", background: "#f8f9fb", fontFamily: "'DM Sans', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono&display=swap" rel="stylesheet" />
      {toast && <div style={{ position: "fixed", top: 20, right: 20, zIndex: 1000, padding: "12px 24px", borderRadius: 10, color: "#fff", fontSize: 14, fontWeight: 500, background: toast.type === "error" ? "#e74c3c" : "#27ae60", boxShadow: "0 4px 20px rgba(0,0,0,0.15)" }}>{toast.msg}</div>}
      <div style={{ position: "fixed", left: 0, top: 0, bottom: 0, width: 240, background: "#1a1d29", padding: "24px 0", display: "flex", flexDirection: "column", zIndex: 100 }}>
        <div style={{ padding: "0 24px", marginBottom: 32 }}>
          <h1 style={{ color: "#fff", fontSize: 20, fontWeight: 700, margin: 0 }}>⚡ eGenya</h1>
          <p style={{ color: "#6b7280", fontSize: 12, margin: "4px 0 0" }}>Sistema de Facturación</p>
          {envInfo?.is_dev && <span style={{ display:"inline-block", marginTop:6, padding:"2px 8px", borderRadius:4, fontSize:10, fontWeight:700, background:"#f59e0b", color:"#000", letterSpacing:1 }}>DEV</span>}
        </div>
        {[{ id: "dashboard", icon: "📊", label: "Dashboard" },{ id: "sales_panel", icon: "📈", label: "Panel" },{ id: "clients", icon: "👥", label: "Clientes" },{ id: "invoices_list", icon: "📄", label: "Facturas" },{ id: "conciliation", icon: "🏦", label: "Conciliación" },{ id: "communications", icon: "📨", label: "Comunicaciones" },{ id: "new_client", icon: "➕", label: "Nuevo Cliente" },{ id: "templates", icon: "✉️", label: "Plantillas" },{ id: "settings", icon: "⚙️", label: "Configuración" }].map(item => (
          <button key={item.id} onClick={() => navTo(item.id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 24px", background: page === item.id ? "rgba(99,102,241,0.15)" : "transparent", border: "none", cursor: "pointer", color: page === item.id ? "#818cf8" : "#9ca3af", fontSize: 14, fontWeight: page === item.id ? 600 : 400, textAlign: "left", borderRight: page === item.id ? "3px solid #818cf8" : "3px solid transparent", fontFamily: "'DM Sans', sans-serif" }}>
            <span style={{ fontSize: 18 }}>{item.icon}</span>{item.label}
          </button>
        ))}
        {ufValue && <div style={{ margin: "auto 24px 8px", padding: 16, background: "rgba(99,102,241,0.1)", borderRadius: 10, border: "1px solid rgba(99,102,241,0.2)" }}>
          <p style={{ color: "#6b7280", fontSize: 11, margin: 0, textTransform: "uppercase", letterSpacing: 1 }}>UF Hoy</p>
          <p style={{ color: "#818cf8", fontSize: 20, fontWeight: 700, margin: "4px 0 0", fontFamily: "DM Mono" }}>${ufValue?.toLocaleString("es-CL", { minimumFractionDigits: 2 })}</p>
        </div>}
        <div style={{ padding: "12px 24px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <p style={{ color: "#9ca3af", fontSize: 12, margin: "0 0 8px" }}>{user?.name || user?.email}</p>
          <button onClick={logout} style={{ background: "none", border: "none", color: "#ef4444", fontSize: 12, cursor: "pointer", padding: 0, fontFamily: "'DM Sans', sans-serif" }}>Cerrar sesión</button>
        </div>
      </div>
      <div style={{ marginLeft: 240, padding: "32px 40px" }}>
        {page === "dashboard" && <Dashboard clients={clients} invoices={invoices} ufValue={ufValue} navTo={navTo} loadInvoices={loadInvoices} />}
        {page === "clients" && <ClientList clients={clients} navTo={navTo} />}
        {page === "new_client" && <ClientForm onSave={async (data) => { const res = await api("/clients", "POST", data); if (res.success) { showToast("Cliente creado"); loadClients(); navTo("clients"); } else showToast(res.error, "error"); }} onCancel={() => navTo("clients")} showToast={showToast} />}
        {page === "edit_client" && selectedClient && <ClientForm client={selectedClient} onSave={async (data) => { const res = await api(`/clients/${selectedClient.client_id}`, "PUT", data); if (res.success) { showToast("Cliente actualizado"); loadClients(); navTo("clients"); } else showToast(res.error, "error"); }} onCancel={() => navTo("clients")} showToast={showToast} />}
        {page === "client_detail" && selectedClient && <ClientDetail client={selectedClient} invoices={invoices} navTo={navTo} showToast={showToast} onRefresh={loadClients} loadInvoices={loadInvoices} />}
        {page === "sales_panel" && <SalesPanel invoices={invoices} clients={clients} />}
        {page === "invoices_list" && <InvoiceList invoices={invoices} clients={clients} showToast={showToast} loadInvoices={loadInvoices} />}
        {page === "conciliation" && <Conciliation showToast={showToast} onRefresh={loadInvoices} />}
        {page === "communications" && <CommunicationsLog />}
        {page === "templates" && <TemplateEditor showToast={showToast} />}
        {page === "settings" && <SystemSettings showToast={showToast} />}
      </div>
      <style>{`* { box-sizing: border-box; } button:hover { opacity: 0.85; } input, select, textarea { font-family: 'DM Sans', sans-serif; }`}</style>
    </div>
  );
}

function Dashboard({ clients, invoices, ufValue, navTo, loadInvoices }) {
  const [billingData, setBillingData] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [billingError, setBillingError] = useState(false);
  const [unbilledData, setUnbilledData] = useState(null);
  const [loadingUnbilled, setLoadingUnbilled] = useState(false);
  const [unbilledError, setUnbilledError] = useState(false);
  const [reviewClient, setReviewClient] = useState(null);
  const [emitting, setEmitting] = useState(null);
  const [siiNumber, setSiiNumber] = useState("");
  const [editGlosa, setEditGlosa] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [invoiceServiceMonth, setInvoiceServiceMonth] = useState("");
  const [invoiceServiceYear, setInvoiceServiceYear] = useState("");
  const [customNetClp, setCustomNetClp] = useState("");
  const [showToastLocal, setShowToastLocal] = useState(null);
  const [selectedDays, setSelectedDays] = useState(7);

  const activeClients = clients.filter(c => c.status === "active");
  const pendingInvoices = invoices.filter(i => i.payment_status === "pending" || i.payment_status === "overdue" || i.payment_status === "collection");
  const totalCPC = pendingInvoices.reduce((sum, i) => sum + (Number(i.total_clp) || 0), 0);
  const overdueInvoices = invoices.filter(i => i.payment_status === "overdue");
  const collectionInvoices = invoices.filter(i => i.payment_status === "collection");
  const overdueCollectionTotal = [...overdueInvoices, ...collectionInvoices].reduce((sum, i) => sum + (Number(i.total_clp) || 0), 0);

  const loadBillingToday = useCallback(async () => {
    setLoadingBilling(true);
    setBillingError(false);
    try {
      const res = await api(`/billing/today?days=${selectedDays}`);
      if (res.success) setBillingData(res.data);
      else setBillingError(true);
    } catch (e) {
      setBillingError(true);
    } finally {
      setLoadingBilling(false);
    }
  }, [selectedDays]);

  const loadUnbilled = useCallback(async () => {
    setLoadingUnbilled(true);
    setUnbilledError(false);
    try {
      const res = await api("/billing/unbilled");
      if (res.success) setUnbilledData(res.data);
      else setUnbilledError(true);
    } catch (e) {
      setUnbilledError(true);
    } finally {
      setLoadingUnbilled(false);
    }
  }, []);

  const refreshDashboard = useCallback(() => {
    loadBillingToday();
    loadUnbilled();
    loadInvoices();
  }, [loadBillingToday, loadUnbilled, loadInvoices]);

  useEffect(() => { if (!billingData || billingData.days_ahead !== selectedDays) loadBillingToday(); }, [selectedDays]);
  useEffect(() => { loadUnbilled(); }, []);

  const openReview = (c) => {
    const today = new Date().toISOString().split("T")[0];
    const ym = c.year_month || today.slice(0,7);
    setReviewClient(c);
    setEditGlosa(c.glosa);
    setSiiNumber("");
    setInvoiceDate(today);
    setInvoiceServiceMonth(ym.split("-")[1] || String(new Date().getMonth()+1).padStart(2,"0"));
    setInvoiceServiceYear(ym.split("-")[0] || String(new Date().getFullYear()));
    setCustomNetClp(String(c.net_clp || ""));
  };
  const closeReview = () => { setReviewClient(null); };

  const emitInvoice = async (c) => {
    const clientFull = clients.find(cl => cl.client_id === c.client_id) || c;
    if (!clientFull.giro || !clientFull.direccion || !clientFull.comuna || !clientFull.ciudad) {
      alert("Faltan datos del cliente requeridos para facturar (Giro, Dirección, Comuna, Ciudad). Edita el cliente primero.");
      return;
    }
    setEmitting(c.client_id);
    const issueDate = invoiceDate || new Date().toISOString().split("T")[0];
    const dueDate = new Date(issueDate);
    dueDate.setDate(dueDate.getDate() + (c.payment_terms || 30));
    const serviceYM = c.year_month || "";
    const net = parseInt(customNetClp) || c.net_clp;
    const iva = Math.round(net * 0.19);
    const total = net + iva;
    const res = await api("/billing/emit", "POST", {
      client_id: c.client_id, emit_sii: true, issue_date: issueDate,
      due_date: dueDate.toISOString().split("T")[0],
      amount_uf: c.amount_uf, uf_value: c.uf_value, net_clp: net,
      iva_clp: iva, total_clp: total, glosa: editGlosa,
      items: [{ nombre: editGlosa, cantidad: 1, precio: net }],
      payment_terms: c.payment_terms, oc_number: c.oc_reference?.number || "",
      year_month: serviceYM,
    });
    if (res.success) {
      setShowToastLocal(`✅ Factura N° ${res.data?.sii_number} emitida para ${c.client_name}`);
      setTimeout(() => setShowToastLocal(null), 4000);
      setReviewClient(null);
      loadBillingToday();
      loadUnbilled();
      loadInvoices();
      // Preguntar si enviar correo
      if (c.invoice_emails?.length && res.data?.invoice_id) {
        if (window.confirm(`¿Enviar correo de factura N° ${res.data?.sii_number} a ${c.invoice_emails.join(", ")}?`)) {
          const emailRes = await api(`/invoices/${res.data.invoice_id}/send-email`, "POST", {});
          if (emailRes.success) setShowToastLocal(`📧 Correo enviado (${emailRes.attachments_count} adjuntos${emailRes.attachment_errors?.length ? " ⚠️ " + emailRes.attachment_errors.join(" | ") : ""}`);
          else alert(`Error al enviar correo: ${emailRes.error || "Error desconocido"}`);
        }
      }
    } else {
      alert(res.error || "Error al registrar factura");
    }
    setEmitting(null);
  };

  const cards = [
    { label: "Clientes Activos", value: activeClients.length, color: "#6366f1" },
    { label: "Facturas Pendientes", value: pendingInvoices.length, color: "#f59e0b" },
    { label: `Vencidas / Cobranza — $${overdueCollectionTotal.toLocaleString("es-CL")}`, value: `${overdueInvoices.length} / ${collectionInvoices.length}`, color: "#ef4444" },
    { label: "CPC Total", value: `$${totalCPC.toLocaleString("es-CL")}`, color: "#10b981" },
  ];

  const billingClients = billingData?.clients || [];
  const readyClients = billingClients.filter(c => c.can_bill);
  const blockedClients = billingClients.filter(c => !c.can_bill);

  return (
    <div>
      {showToastLocal && <div style={{ position:"fixed",top:20,right:20,zIndex:1000,padding:"12px 24px",borderRadius:10,color:"#fff",fontSize:14,fontWeight:500,background:"#27ae60",boxShadow:"0 4px 20px rgba(0,0,0,0.15)" }}>{showToastLocal}</div>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a1d29", margin: 0 }}>Dashboard</h2>
        <button onClick={refreshDashboard} disabled={loadingBilling || loadingUnbilled}
          style={{ display: "flex", alignItems: "center", gap: 6, background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 8, padding: "7px 14px", fontSize: 13, fontWeight: 500, color: "#374151", cursor: (loadingBilling || loadingUnbilled) ? "not-allowed" : "pointer", opacity: (loadingBilling || loadingUnbilled) ? 0.6 : 1 }}>
          <span style={{ display: "inline-block", animation: (loadingBilling || loadingUnbilled) ? "spin 1s linear infinite" : "none" }}>↻</span>
          {(loadingBilling || loadingUnbilled) ? "Actualizando..." : "Actualizar"}
        </button>
      </div>
      <p style={{ color: "#6b7280", margin: "0 0 28px", fontSize: 14 }}>{new Date().toLocaleDateString("es-CL", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, marginBottom: 32 }}>
        {cards.map((c, i) => (<div key={i} style={{ background: "#fff", borderRadius: 14, padding: "20px 24px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)", borderLeft: `4px solid ${c.color}` }}><p style={{ color: "#6b7280", fontSize: 12, margin: 0, textTransform: "uppercase", letterSpacing: 0.5 }}>{c.label}</p><p style={{ fontSize: 28, fontWeight: 700, color: "#1a1d29", margin: "8px 0 0", fontFamily: "DM Mono" }}>{c.value}</p></div>))}
      </div>

      {/* FACTURACIÓN VENCIDA */}
      {(loadingUnbilled || unbilledError || (unbilledData?.clients?.length > 0)) && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", marginBottom: 24, borderLeft: "4px solid #ef4444" }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, margin: "0 0 16px", color: "#ef4444" }}>
            ⚠️ Facturación Vencida ({unbilledData?.clients?.length || 0})
          </h3>
          {loadingUnbilled ? <p style={{ color: "#9ca3af", fontSize: 14 }}>Cargando...</p> :
           unbilledError ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0" }}>
              <span style={{ color: "#ef4444", fontSize: 14 }}>No se pudo cargar. Posible inicio en frío del servidor.</span>
              <button onClick={loadUnbilled} style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#ef4444", borderRadius: 6, padding: "5px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>↻ Reintentar</button>
            </div>
           ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#fff5f5" }}>
                  {["Cliente", "Período", "Días vencida", "Monto", "Docs faltantes", ""].map((h, i) => (
                    <th key={i} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#991b1b", borderBottom: "1px solid #fecaca" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {unbilledData.clients.map((c, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #fef2f2" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 500, color: "#1a1d29" }}>{c.client_name}</td>
                    <td style={{ padding: "10px 12px", color: "#6b7280" }}>{c.month}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ background: "#fef2f2", color: "#ef4444", borderRadius: 6, padding: "2px 8px", fontWeight: 600 }}>{c.days_overdue} días</span>
                    </td>
                    <td style={{ padding: "10px 12px", fontFamily: "DM Mono", color: "#1a1d29" }}>${c.total_clp.toLocaleString("es-CL")}</td>
                    <td style={{ padding: "10px 12px", color: c.missing_docs.length ? "#f59e0b" : "#10b981", fontSize: 12 }}>
                      {c.missing_docs.length ? c.missing_docs.join(", ") : "✓ Completo"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        {c.can_bill && (
                          <button onClick={() => openReview(c)} style={{ background: "#ef4444", color: "#fff", border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                            Facturar
                          </button>
                        )}
                        <button onClick={() => navTo("client_detail", clients.find(cl => cl.client_id === c.client_id))} style={{ background: "none", border: "1px solid #fecaca", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#ef4444" }}>
                          Ir a cuenta
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* FACTURACIÓN DEL DÍA */}
      <div style={{ background: "#fff", borderRadius: 14, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: "#1a1d29" }}>
            🔔 Facturación Próxima {billingData ? `— ${billingData.month}` : ""}
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {billingData && <span style={{ fontSize: 13, color: "#6b7280" }}>UF: <strong style={{ color: "#6366f1", fontFamily: "DM Mono" }}>${billingData.uf_value?.toLocaleString("es-CL",{minimumFractionDigits:2})}</strong></span>}
            <div style={{ display: "flex", gap: 4 }}>
              {[{v:7,l:"7 días"},{v:14,l:"14 días"},{v:30,l:"30 días"}].map(opt => (
                <button key={opt.v} onClick={() => setSelectedDays(opt.v)}
                  style={{ padding:"4px 10px", borderRadius:6, fontSize:12, fontWeight: selectedDays===opt.v?600:400, border: selectedDays===opt.v?"2px solid #6366f1":"1px solid #e5e7eb", background: selectedDays===opt.v?"#eef2ff":"#fff", color: selectedDays===opt.v?"#6366f1":"#6b7280", cursor:"pointer" }}>{opt.l}</button>
              ))}
            </div>
          </div>
        </div>

        {loadingBilling ? <p style={{ color: "#6b7280", textAlign: "center", padding: 20 }}>Cargando...</p> :
         billingError ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: 32, flexDirection: "column" }}>
            <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>No se pudo cargar. El servidor puede estar iniciándose.</p>
            <button onClick={loadBillingToday} style={{ background: "#eef2ff", border: "1px solid #c7d2fe", color: "#6366f1", borderRadius: 6, padding: "6px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>↻ Reintentar</button>
          </div>
         ) :
         billingClients.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <p style={{ fontSize: 36, margin: "0 0 8px" }}>✅</p>
            <p style={{ color: "#6b7280", fontSize: 14 }}>No hay clientes para facturar en los próximos {selectedDays} días</p>
          </div>
        ) : (
          <div>
            {/* Listos para facturar */}
            {readyClients.length > 0 && readyClients.map(c => (
              <div key={c.client_id} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 12, background: "#f8fffe" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 15, color: "#1a1d29" }}>{c.client_name}</span>
                    <span style={{ marginLeft: 8, padding: "2px 6px", borderRadius: 4, fontSize: 11, fontWeight: 600, background: c.billing_day === new Date().getDate() ? "#d1fae5" : "#fef3c7", color: c.billing_day === new Date().getDate() ? "#065f46" : "#92400e" }}>Día {c.billing_day}{c.billing_day === new Date().getDate() ? " (hoy)" : ""}</span>
                    <span style={{ marginLeft: 8, fontSize: 12, color: "#6b7280" }}>RUT: {c.rut}</span>
                    <span style={{ marginLeft: 8, padding: "3px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, background: "#d1fae5", color: "#065f46" }}>Tipo {c.billing_type}</span>
                    {c.oc_reference && <span style={{ marginLeft: 8, fontSize: 12, color: "#6b7280" }}>OC: {c.oc_reference.number}</span>}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ textAlign: "right" }}>
                      <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>{c.amount_uf} UF</p>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "#1a1d29", fontFamily: "DM Mono" }}>${c.total_clp?.toLocaleString("es-CL")}</p>
                    </div>
                    <button onClick={() => openReview(c)} style={{ background: "#6366f1", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>📋 Revisar</button>
                  </div>
                </div>
              </div>
            ))}

            {/* Bloqueados por documentos faltantes */}
            {blockedClients.length > 0 && (<>
              <p style={{ fontSize: 13, fontWeight: 600, color: "#ef4444", margin: "20px 0 8px" }}>⚠️ No se pueden facturar (documentos faltantes):</p>
              {blockedClients.map(c => (
                <div key={c.client_id} style={{ border: "1px solid #fee2e2", borderRadius: 10, padding: 14, marginBottom: 8, background: "#fff5f5" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <span style={{ fontWeight: 500, color: "#1a1d29" }}>{c.client_name}</span>
                      <span style={{ marginLeft: 8, padding: "2px 6px", borderRadius: 4, fontSize: 11, fontWeight: 600, background: c.billing_day === new Date().getDate() ? "#d1fae5" : "#fef3c7", color: c.billing_day === new Date().getDate() ? "#065f46" : "#92400e" }}>Día {c.billing_day}{c.billing_day === new Date().getDate() ? " (hoy)" : ""}</span>
                      <span style={{ marginLeft: 8, fontSize: 12, color: "#ef4444" }}>Falta: {c.missing_docs.join(", ")}</span>
                    </div>
                    <button onClick={() => navTo("client_detail", clients.find(cl => cl.client_id === c.client_id))} style={{ background: "none", border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer", color: "#6b7280" }}>Ir a ficha</button>
                  </div>
                </div>
              ))}
            </>)}
          </div>
        )}
      </div>

      {/* MODAL DE REVISIÓN */}
      {reviewClient && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={closeReview}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 32, maxWidth: 600, width: "90%", maxHeight: "80vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1a1d29", margin: 0 }}>📋 Revisar Factura</h3>
              <button onClick={closeReview} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#6b7280" }}>✕</button>
            </div>

            <div style={{ background: "#f8f9fb", borderRadius: 10, padding: 20, marginBottom: 20 }}>
              <h4 style={{ margin: "0 0 12px", fontSize: 14, color: "#6366f1", fontWeight: 600 }}>Datos del Cliente</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 14 }}>
                <div><span style={{ color: "#6b7280", fontSize: 12 }}>Cliente:</span><br /><strong>{reviewClient.client_name}</strong></div>
                <div><span style={{ color: "#6b7280", fontSize: 12 }}>RUT:</span><br /><strong style={{ fontFamily: "DM Mono" }}>{reviewClient.rut}</strong></div>
                <div><span style={{ color: "#6b7280", fontSize: 12 }}>Tipo:</span><br /><strong>{reviewClient.billing_type}</strong></div>
                <div><span style={{ color: "#6b7280", fontSize: 12 }}>Pago:</span><br /><strong>{reviewClient.payment_terms} días</strong></div>
              </div>
              {reviewClient.oc_reference && (
                <div style={{ marginTop: 8 }}><span style={{ color: "#6b7280", fontSize: 12 }}>OC:</span> <strong>N° {reviewClient.oc_reference.number}</strong> {reviewClient.oc_reference.date && <span style={{ color: "#6b7280", fontSize: 12 }}>({reviewClient.oc_reference.date})</span>}</div>
              )}
            </div>

            {reviewClient.invoice_class === "exportacion" ? (
              <div style={{ background: "#f0f4ff", borderRadius: 10, padding: 20, marginBottom: 20 }}>
                <h4 style={{ margin: "0 0 12px", fontSize: 14, color: "#6366f1", fontWeight: 600 }}>Monto (Exportación · DTE 110)</h4>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#1a1d29" }}>
                  Total: <span style={{ fontFamily: "DM Mono", color: "#6366f1" }}>US$ {Number(reviewClient.amount_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                  <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 600, color: "#16a34a" }}>EXENTO DE IVA</span>
                </div>
                <p style={{ margin: "10px 0 0", fontSize: 12, color: "#6b7280" }}>
                  El equivalente en CLP (dólar observado del día) se registra automáticamente en la sección <em>OtraMoneda</em> del DTE.
                </p>
              </div>
            ) : (
            <div style={{ background: "#f0f4ff", borderRadius: 10, padding: 20, marginBottom: 20 }}>
              <h4 style={{ margin: "0 0 12px", fontSize: 14, color: "#6366f1", fontWeight: 600 }}>Montos</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 14, marginBottom: 12 }}>
                <div style={{ color: "#6b7280" }}>UF: <strong style={{ fontFamily: "DM Mono", color: "#1a1d29" }}>{reviewClient.amount_uf}</strong></div>
                <div style={{ color: "#6b7280" }}>Valor UF: <strong style={{ fontFamily: "DM Mono", color: "#1a1d29" }}>${reviewClient.uf_value?.toLocaleString("es-CL",{minimumFractionDigits:2})}</strong></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "end" }}>
                <div>
                  <label style={{ fontSize: 12, color: "#6b7280", display: "block", marginBottom: 4 }}>
                    Monto Neto (CLP)
                    {parseInt(customNetClp) !== reviewClient.net_clp && <span style={{ marginLeft: 6, fontSize: 11, color: "#f59e0b", fontWeight: 600 }}>modificado · recomendado: ${reviewClient.net_clp?.toLocaleString("es-CL")}</span>}
                  </label>
                  <input
                    type="number"
                    value={customNetClp}
                    onChange={e => setCustomNetClp(e.target.value)}
                    style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: `1px solid ${parseInt(customNetClp) !== reviewClient.net_clp ? "#f59e0b" : "#c7d2fe"}`, fontSize: 15, fontFamily: "DM Mono", fontWeight: 600, outline: "none", background: "#fff" }}
                  />
                </div>
                <div style={{ fontSize: 14, color: "#6b7280" }}>
                  <div>IVA 19%: <strong style={{ fontFamily: "DM Mono", color: "#1a1d29" }}>${Math.round((parseInt(customNetClp)||0)*0.19).toLocaleString("es-CL")}</strong></div>
                </div>
              </div>
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: "2px solid #c7d2fe", fontSize: 18, fontWeight: 700, color: "#1a1d29" }}>
                Total: <span style={{ fontFamily: "DM Mono", color: "#6366f1" }}>${((parseInt(customNetClp)||0) + Math.round((parseInt(customNetClp)||0)*0.19)).toLocaleString("es-CL")}</span>
              </div>
            </div>
            )}

            <div style={{ background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8, padding:"12px 16px", marginBottom:16, display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:"#1d4ed8", display:"block", marginBottom:4 }}>📅 Fecha de factura</label>
                <input type="date" value={invoiceDate} onChange={e=>setInvoiceDate(e.target.value)} style={{ width:"100%", padding:"8px 12px", borderRadius:7, border:"1px solid #bfdbfe", fontSize:13, outline:"none", background:"#fff" }} />
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:"#1d4ed8", display:"block", marginBottom:4 }}>📋 Mes de servicio</label>
                <select value={invoiceServiceMonth} onChange={e=>setInvoiceServiceMonth(e.target.value)} style={{ width:"100%", padding:"8px 12px", borderRadius:7, border:"1px solid #bfdbfe", fontSize:13, outline:"none", background:"#fff" }}>
                  {["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"].map((mn,i)=><option key={i} value={String(i+1).padStart(2,"0")}>{mn}</option>)}
                  <option value="otro">Otro (no asignar a mes)</option>
                </select>
              </div>
              {invoiceServiceMonth !== "otro" && (
                <div>
                  <label style={{ fontSize:12, fontWeight:600, color:"#1d4ed8", display:"block", marginBottom:4 }}>Año de servicio</label>
                  <input type="number" value={invoiceServiceYear} onChange={e=>setInvoiceServiceYear(e.target.value)} min="2020" max="2030" style={{ width:"100%", padding:"8px 12px", borderRadius:7, border:"1px solid #bfdbfe", fontSize:13, outline:"none", background:"#fff", fontFamily:"DM Mono" }} />
                </div>
              )}
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Glosa / Descripción</label>
              <textarea value={editGlosa} onChange={e => setEditGlosa(e.target.value)} rows={3}
                style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14, outline: "none", resize: "vertical", fontFamily: "'DM Sans', sans-serif" }} />
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => emitInvoice(reviewClient)} disabled={!!emitting}
                style={{ flex: 1, background: "#10b981", color: "#fff", border: "none", borderRadius: 10, padding: "14px 24px", fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: emitting ? 0.6 : 1 }}>
                {emitting ? "Facturando..." : "🧾 Facturar"}
              </button>
              <button onClick={closeReview} style={{ background: "none", border: "1px solid #e5e7eb", borderRadius: 10, padding: "14px 24px", fontSize: 14, cursor: "pointer", color: "#6b7280" }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* COBRANZA - Facturas Vencidas y en Cobranza */}
      {(overdueInvoices.length > 0 || collectionInvoices.length > 0) && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: "#ef4444" }}>
              Seguimiento de Cobranza ({overdueInvoices.length + collectionInvoices.length})
            </h3>
            <button onClick={async () => {
              if (!window.confirm("¿Ejecutar verificación de cobranza? Se enviarán recordatorios a clientes con facturas vencidas.")) return;
              const res = await api("/daily-check", "POST", {});
              if (res.success) {
                const d = res.data;
                const msg = `Cobranza: ${d.collection_actions?.length || 0} acciones ejecutadas`;
                alert(msg);
                loadInvoices();
              } else alert(res.error || "Error");
            }} style={{ background: "#ef4444", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>⚡ Ejecutar Cobranza</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[...overdueInvoices, ...collectionInvoices]
              .sort((a,b) => (a.due_date||"") < (b.due_date||"") ? -1 : 1)
              .map(inv => {
                const cl = clients.find(c => c.client_id === inv.client_id);
                const dueDate = inv.due_date ? new Date(inv.due_date + "T12:00:00") : null;
                const daysOverdue = dueDate ? Math.floor((new Date() - dueDate) / (1000*60*60*24)) : 0;
                const urgencyColor = daysOverdue >= 30 ? "#dc2626" : daysOverdue >= 15 ? "#f59e0b" : "#6b7280";
                const isCollection = inv.payment_status === "collection";
                const log = inv.collection_log || [];
                const ACTION_INFO = {
                  recordatorio: { label: "Recordatorio enviado", color: "#f59e0b", bg: "#fffbeb" },
                  alerta_interna: { label: "Alerta interna", color: "#6366f1", bg: "#eef2ff" },
                  ultimo_aviso: { label: "Último aviso enviado", color: "#dc2626", bg: "#fef2f2" },
                };
                const ACTION_ABBR = { recordatorio: "R", alerta_interna: "A", ultimo_aviso: "U" };
                const abbrCounts = {};
                const logBadges = log.map((entry, i) => {
                  const abbr = ACTION_ABBR[entry.action] || "?";
                  abbrCounts[abbr] = (abbrCounts[abbr] || 0) + 1;
                  const info = ACTION_INFO[entry.action] || { color: "#6b7280", bg: "#f3f4f6" };
                  return (
                    <span key={i} title={info.label} style={{ padding: "3px 8px", borderRadius: 6, background: info.bg, border: `1px solid ${info.color}40`, fontSize: 12, color: info.color, fontWeight: 600, whiteSpace: "nowrap" }}>
                      {abbr}{abbrCounts[abbr]} {entry.date}
                    </span>
                  );
                });
                return (
                  <div key={inv.invoice_id} style={{ border: `1px solid ${isCollection ? "#fce7f3" : "#fee2e2"}`, borderRadius: 10, padding: "12px 16px", background: isCollection ? "#fdf4ff" : "#fff9f9" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        <span style={{ fontWeight: 700, fontSize: 14, color: "#1a1d29" }}>{cl?.client_name || "—"}</span>
                        <span style={{ fontSize: 13, color: "#6b7280" }}>Factura #{inv.sii_number || "—"}</span>
                        <span style={{ fontWeight: 600, fontSize: 13, color: "#374151" }}>${Number(inv.total_clp || 0).toLocaleString("es-CL")}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        {log.length === 0
                          ? <span style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic" }}>Sin correos enviados aún</span>
                          : logBadges}
                        <span style={{ fontSize: 12, color: "#6b7280" }}>Venció: {inv.due_date}</span>
                        <span style={{ padding: "3px 10px", borderRadius: 6, fontSize: 12, fontWeight: 700, color: "#fff", background: urgencyColor }}>{daysOverdue} días</span>
                        <span style={{ padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, color: isCollection ? "#9d174d" : "#991b1b", background: isCollection ? "#fce7f3" : "#fee2e2" }}>
                          {isCollection ? "En Cobranza" : "Vencida"}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

function ClientList({ clients, navTo }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const filtered = clients.filter(c => {
    const matchSearch = c.client_name?.toLowerCase().includes(search.toLowerCase()) || c.rut?.includes(search);
    const matchStatus = statusFilter === "all" || c.status === statusFilter;
    return matchSearch && matchStatus;
  });
  const counts = { all: clients.length, active: clients.filter(c => c.status === "active").length, paused: clients.filter(c => c.status === "paused").length, inactive: clients.filter(c => c.status === "inactive").length };
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a1d29", margin: 0 }}>Clientes</h2>
        <button onClick={() => navTo("new_client")} style={{ background: "#6366f1", color: "#fff", border: "none", borderRadius: 10, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>+ Nuevo Cliente</button>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[{ v: "all", l: "Todos", c: "#6366f1" },{ v: "active", l: "Activos", c: "#10b981" },{ v: "paused", l: "Pausados", c: "#f59e0b" },{ v: "inactive", l: "Baja", c: "#ef4444" }].map(f => (
          <button key={f.v} onClick={() => setStatusFilter(f.v)} style={{ padding: "6px 14px", borderRadius: 8, fontSize: 13, fontWeight: statusFilter === f.v ? 600 : 400, border: statusFilter === f.v ? `2px solid ${f.c}` : "1px solid #e5e7eb", background: statusFilter === f.v ? f.c + "15" : "#fff", color: statusFilter === f.v ? f.c : "#6b7280", cursor: "pointer" }}>{f.l} ({counts[f.v]})</button>
        ))}
      </div>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre o RUT..." style={{ width: "100%", padding: "12px 16px", borderRadius: 10, border: "1px solid #e5e7eb", fontSize: 14, marginBottom: 20, outline: "none", background: "#fff" }} />
      <div style={{ background: "#fff", borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#f8f9fb" }}>{["Cliente","RUT","Vertical","Día","UF","Tipo","Pago","Estado",""].map((h,i)=>(<th key={i} style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>{h}</th>))}</tr></thead>
          <tbody>
            {filtered.sort((a,b)=>(a.client_name||"").localeCompare(b.client_name||"")).map(c => {
              const st = STATUS_OPTIONS.find(s=>s.value===c.status) || STATUS_OPTIONS[0];
              return (<tr key={c.client_id} style={{ borderTop: "1px solid #f3f4f6", opacity: c.status === "inactive" ? 0.5 : 1 }}>
                <td style={{ padding: "14px 16px", fontWeight: 500, color: "#1a1d29" }}>{c.client_name}</td>
                <td style={{ padding: "14px 16px", color: "#6b7280", fontFamily: "DM Mono", fontSize: 13 }}>{c.rut}</td>
                <td style={{ padding: "14px 16px" }}><span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: VERTICAL_COLORS[c.vertical] ? VERTICAL_COLORS[c.vertical] + "18" : "#f3f4f6", color: VERTICAL_COLORS[c.vertical] || "#6b7280" }}>{c.vertical || "—"}</span></td>
                <td style={{ padding: "14px 16px", color: "#6b7280" }}>{c.billing_day}</td>
                <td style={{ padding: "14px 16px", fontWeight: 600, color: "#6366f1", fontFamily: "DM Mono" }}>{c.amount_uf}</td>
                <td style={{ padding: "14px 16px" }}><span style={{ padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: c.billing_type==="A"?"#e0e7ff":c.billing_type==="B"?"#fef3c7":c.billing_type==="C"?"#d1fae5":"#fce7f3", color: c.billing_type==="A"?"#4338ca":c.billing_type==="B"?"#92400e":c.billing_type==="C"?"#065f46":"#9d174d" }}>Tipo {c.billing_type}</span></td>
                <td style={{ padding: "14px 16px", color: "#6b7280" }}>{c.payment_terms}d</td>
                <td style={{ padding: "14px 16px" }}><span style={{ padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: st.bg, color: st.color }}>{st.label}</span></td>
                <td style={{ padding: "14px 16px" }}><button onClick={()=>navTo("client_detail",c)} style={{ background:"none", border:"1px solid #e5e7eb", borderRadius:6, padding:"6px 12px", fontSize:12, cursor:"pointer", color:"#6b7280", marginRight:4 }}>Ver</button><button onClick={()=>navTo("edit_client",c)} style={{ background:"none", border:"1px solid #e5e7eb", borderRadius:6, padding:"6px 12px", fontSize:12, cursor:"pointer", color:"#6b7280" }}>Editar</button></td>
              </tr>);
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <div style={{ padding: 40, textAlign: "center", color: "#6b7280" }}>{clients.length === 0 ? "No hay clientes registrados." : "No se encontraron resultados"}</div>}
      </div>
    </div>
  );
}

function PdfUploadButton({ clientId, docType, yearMonth, currentKey, onUploaded, onExtracted, showToast }) {
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (file.type !== "application/pdf") { showToast("Solo PDF", "error"); return; }
    setUploading(true);
    try {
      const uploadPromise = uploadPDF(file, clientId, docType, yearMonth);
      let extractPromise = Promise.resolve(null);
      if (onExtracted && docType === "oc") {
        const fd = new FormData(); fd.append("file", file);
        extractPromise = fetch(`${API_URL}/extract-oc`, {
          method: "POST",
          headers: { "Authorization": `Bearer ${sessionStorage.getItem("egenya_token")}` },
          body: fd,
        }).then(r => r.json()).catch(() => null);
        setExtracting(true);
      }
      const [key, extractRes] = await Promise.all([uploadPromise, extractPromise]);
      setExtracting(false);
      showToast("PDF subido");
      if (onUploaded) onUploaded(key);
      if (onExtracted && docType === "oc") {
        if (!extractRes) {
          showToast("No se pudo conectar con el servicio de extracción", "error");
        } else if (!extractRes.success) {
          showToast("Error al leer el PDF: " + (extractRes.error || "desconocido"), "error");
        } else {
          const d = extractRes.data || {};
          if (d.doc_number || d.doc_date || d.glosa) {
            onExtracted(d);
            showToast("✨ Datos detectados automáticamente");
          } else {
            showToast("PDF subido (no se detectaron datos — ingresa manualmente)", "info");
          }
        }
      }
    } catch (err) { showToast("Error: " + err.message, "error"); }
    setUploading(false); setExtracting(false); e.target.value = "";
  };
  const busy = uploading || extracting;
  const label = uploading ? "Subiendo..." : extracting ? "Leyendo OC..." : (currentKey ? "Cambiar" : "📤 Subir PDF");
  return (<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
    {currentKey && <button onClick={() => downloadPDF(currentKey)} style={{ background: "none", border: "none", cursor: "pointer", color: "#6366f1", fontSize: 12, textDecoration: "underline", padding: 0 }}>📎 Ver PDF</button>}
    <label style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: busy ? "wait" : "pointer", background: currentKey ? "#e0e7ff" : "#f3f4f6", color: currentKey ? "#4338ca" : "#6b7280", border: "1px solid " + (currentKey ? "#c7d2fe" : "#e5e7eb") }}>
      {label}
      <input type="file" accept=".pdf" onChange={handleFile} style={{ display: "none" }} disabled={busy} />
    </label>
  </span>);
}

function ClientForm({ client, onSave, onCancel, showToast }) {
  const isEdit = !!client;
  const [form, setForm] = useState({
    client_name: client?.client_name || "", razon_social: client?.razon_social || client?.client_name || "", rut: client?.rut || "",
    vertical: client?.vertical || "",
    billing_day: client?.billing_day || 1,
    invoice_class: client?.invoice_class || "nacional",
    billing_currency: client?.billing_currency || "UF",
    amount_uf: client?.amount_uf || "",
    amount_clp: client?.amount_clp || "",
    amount_usd: client?.amount_usd || "",
    pais_receptor: client?.pais_receptor || "",
    num_id_receptor: client?.num_id_receptor || "",
    billing_type: client?.billing_type || "A", payment_terms: client?.payment_terms || 30,
    glosa: client?.glosa || "",
    quotation_id: client?.quotation_id || "", quotation_pdf_key: client?.quotation_pdf_key || "",
    invoice_emails: client?.invoice_emails?.join(", ") || "",
    collection_emails: client?.collection_emails?.join(", ") || "",
    hes_contact_email: client?.hes_contact_email || "",
    oc_contact_name: client?.oc_contact_name || "",
    oc_contact_email: client?.oc_contact_email || "",
    oc_request_days: client?.oc_request_days ?? 15,
    modulo: client?.modulo || "eGenya Facility",
    procesos: client?.procesos || "",
    hes_request_days: client?.hes_request_days ?? 5,
    giro: client?.giro || "",
    direccion: client?.direccion || "",
    comuna: client?.comuna || "",
    ciudad: client?.ciudad || "",
    notes: client?.notes || "",
  });
  const handleSubmit = (e) => { e.preventDefault(); onSave({ ...form, billing_day: parseInt(form.billing_day), amount_uf: parseFloat(form.amount_uf) || 0, amount_usd: parseFloat(form.amount_usd) || 0, pais_receptor: parseInt(form.pais_receptor) || 0, payment_terms: parseInt(form.payment_terms), invoice_emails: form.invoice_emails.split(",").map(x=>x.trim()).filter(Boolean), collection_emails: form.collection_emails.split(",").map(x=>x.trim()).filter(Boolean) }); };
  const S = { width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14, outline: "none", background: "#fff" };
  const L = { display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 };
  const bt = BILLING_TYPES[form.billing_type];
  return (
    <div>
      <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a1d29", margin: "0 0 24px" }}>{isEdit ? `Editar: ${client.client_name}` : "Nuevo Cliente"}</h2>
      <form onSubmit={handleSubmit} style={{ background: "#fff", borderRadius: 14, padding: 32, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", maxWidth: 800 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: "#6366f1", margin: "0 0 16px", paddingBottom: 8, borderBottom: "2px solid #e0e7ff" }}>Datos del Cliente</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
          <div><label style={L}>Nombre *</label><input value={form.client_name} onChange={e=>setForm({...form, client_name:e.target.value})} style={S} required /></div>
          <div><label style={L}>Razón social * <span style={{fontWeight:400,color:"#9ca3af"}}>(aparece en la factura)</span></label><input value={form.razon_social} onChange={e=>setForm({...form, razon_social:e.target.value})} style={S} required /></div>
          <div><label style={L}>RUT *{form.invoice_class === "exportacion" && <span style={{fontWeight:400,color:"#9ca3af"}}> (RUT genérico SII para extranjeros)</span>}</label><input value={form.rut} onChange={e=>setForm({...form, rut:e.target.value})} style={form.invoice_class === "exportacion" ? {...S, background:"#f3f4f6", color:"#6b7280"} : S} required readOnly={form.invoice_class === "exportacion"} /></div>
          <div><label style={L}>Vertical *</label><select value={form.vertical} onChange={e=>setForm({...form, vertical:e.target.value})} style={S} required><option value="">— Seleccionar —</option>{VERTICALS.map(v=><option key={v} value={v}>{v}</option>)}</select></div>
          <div><label style={L}>Día Facturación *</label><input type="number" min="1" max="31" value={form.billing_day} onChange={e=>setForm({...form, billing_day:e.target.value})} style={S} required /></div>
          <div><label style={L}>Clasificación *</label><select value={form.invoice_class} onChange={e=>setForm({...form, invoice_class:e.target.value, rut: e.target.value === "exportacion" ? "55555555-5" : (form.rut === "55555555-5" ? "" : form.rut)})} style={S}><option value="nacional">Nacional (Factura electrónica)</option><option value="exportacion">Exportación (Factura de exportación)</option></select></div>
          {form.invoice_class === "exportacion"
            ? <>
                <div><label style={L}>Monto USD *</label><input type="number" step="0.01" value={form.amount_usd} onChange={e=>setForm({...form, amount_usd:e.target.value})} placeholder="Ej: 1500" style={S} /></div>
                <div><label style={L}>País del receptor *</label><select value={form.pais_receptor} onChange={e=>setForm({...form, pais_receptor:e.target.value})} style={S}><option value="">— Seleccionar —</option>{COUNTRIES.map(c=><option key={c.code} value={c.code}>{c.name}</option>)}</select></div>
                <div style={{ gridColumn: "1 / -1" }}><label style={L}>ID tributario del receptor <span style={{fontWeight:400,color:"#9ca3af"}}>(N° de identificación extranjero, ej. Tax ID)</span></label><input value={form.num_id_receptor} onChange={e=>setForm({...form, num_id_receptor:e.target.value})} placeholder="Ej: 331-555555" style={S} /></div>
              </>
            : <div style={{ display:"grid", gridTemplateColumns:"140px 1fr", gap:8 }}>
                <div><label style={L}>Moneda *</label><select value={form.billing_currency} onChange={e=>setForm({...form, billing_currency:e.target.value})} style={S}><option value="UF">UF</option><option value="CLP">CLP (Pesos)</option></select></div>
                {form.billing_currency === "UF"
                  ? <div><label style={L}>Monto UF *</label><input type="number" step="0.01" value={form.amount_uf} onChange={e=>setForm({...form, amount_uf:e.target.value})} style={S} /></div>
                  : <div><label style={L}>Monto CLP *</label><input type="number" step="1" value={form.amount_clp} onChange={e=>setForm({...form, amount_clp:e.target.value})} placeholder="Ej: 1500000" style={S} /></div>
                }
              </div>
          }
          <div style={{ gridColumn: "1 / -1" }}><label style={L}>Giro <span style={{ fontWeight:400, color:"#9ca3af" }}>(requerido para facturar)</span></label><input value={form.giro} onChange={e=>setForm({...form, giro:e.target.value})} placeholder="Ej: Servicios de tecnología y mantenimiento" style={S} /></div>
          <div style={{ gridColumn: "1 / -1" }}><label style={L}>Dirección <span style={{ fontWeight:400, color:"#9ca3af" }}>(requerido para facturar)</span></label><input value={form.direccion} onChange={e=>setForm({...form, direccion:e.target.value})} placeholder="Ej: Av. Apoquindo 4001 Of. 502" style={S} /></div>
          <div><label style={L}>Comuna <span style={{ fontWeight:400, color:"#9ca3af" }}>(requerido para facturar)</span></label><input value={form.comuna} onChange={e=>setForm({...form, comuna:e.target.value})} placeholder="Ej: Las Condes" style={S} /></div>
          <div><label style={L}>Ciudad <span style={{ fontWeight:400, color:"#9ca3af" }}>(requerido para facturar)</span></label><input value={form.ciudad} onChange={e=>setForm({...form, ciudad:e.target.value})} placeholder="Ej: Santiago" style={S} /></div>
        </div>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: "#6366f1", margin: "0 0 16px", paddingBottom: 8, borderBottom: "2px solid #e0e7ff" }}>Facturación</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div><label style={L}>Tipo *</label><select value={form.billing_type} onChange={e=>setForm({...form, billing_type:e.target.value})} style={S}>{Object.entries(BILLING_TYPES).map(([k,v])=><option key={k} value={k}>Tipo {k} — {v.name}</option>)}</select></div>
          <div><label style={L}>Condición Pago *</label><select value={form.payment_terms} onChange={e=>setForm({...form, payment_terms:e.target.value})} style={S}>{PAYMENT_OPTIONS.map(d=><option key={d} value={d}>{d} días</option>)}</select></div>
          {form.billing_type === "A" && <div style={{ gridColumn: "1 / -1" }}><label style={L}>Glosa <span style={{ fontWeight:400, color:"#9ca3af" }}>(descripción que aparece en la factura)</span></label><input value={form.glosa} onChange={e=>setForm({...form, glosa:e.target.value})} placeholder="Ej: Servicio de energía según contrato" style={S} /></div>}
          <div><label style={L}>Módulo *</label><select value={form.modulo} onChange={e=>setForm({...form, modulo:e.target.value})} style={S} required><option value="eGenya Facility">eGenya Facility</option><option value="Orquestra">Orquestra</option><option value="eGenya Security">eGenya Security</option></select></div>
          <div><label style={L}>Procesos <span style={{fontWeight:400,color:"#9ca3af"}}>(cantidad medida)</span></label><input type="number" min="0" value={form.procesos} onChange={e=>setForm({...form, procesos:e.target.value})} placeholder="Ej: 17" style={S} /></div>
          <div><label style={L}>Cotización</label><input value={form.quotation_id} onChange={e=>setForm({...form, quotation_id:e.target.value})} style={S} /></div>
          <div><label style={L}>PDF Cotización</label>{isEdit && client?.client_id ? <PdfUploadButton clientId={client.client_id} docType="quotation" yearMonth="" currentKey={form.quotation_pdf_key} showToast={showToast} onUploaded={key=>setForm({...form, quotation_pdf_key:key})} /> : <p style={{ fontSize: 12, color: "#9ca3af", margin: "10px 0 0" }}>Primero crea el cliente</p>}</div>
        </div>
        {bt.requires_oc && <div style={{ padding: 16, background: "#fffbeb", borderRadius: 10, border: "1px solid #fcd34d", marginBottom: 16, fontSize: 13, color: "#92400e" }}>ℹ️ Requiere <strong>OC</strong>{bt.requires_hes && <> y <strong>HES</strong></>}{bt.requires_ndp && <> y <strong>NdP</strong></>}.</div>}
        <h3 style={{ fontSize: 15, fontWeight: 600, color: "#6366f1", margin: "0 0 4px", paddingBottom: 8, borderBottom: "2px solid #e0e7ff" }}>Correos</h3>
        <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 16px" }}>Para agregar más de un correo, sepáralos con coma. Ej: a@empresa.cl, b@empresa.cl</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16, marginBottom: 24 }}>
          <div><label style={L}>Correos factura *</label><input value={form.invoice_emails} onChange={e=>setForm({...form, invoice_emails:e.target.value})} style={S} required /></div>
          <div><label style={L}>Correos cobranza</label><input value={form.collection_emails} onChange={e=>setForm({...form, collection_emails:e.target.value})} style={S} /></div>
          {(form.billing_type==="C"||form.billing_type==="D") && <div><label style={L}>Contacto HES/NdP</label><input value={form.hes_contact_email} onChange={e=>setForm({...form, hes_contact_email:e.target.value})} style={S} /></div>}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            <div><label style={L}>Nombre contacto OC</label><input value={form.oc_contact_name} onChange={e=>setForm({...form, oc_contact_name:e.target.value})} placeholder="Ej: Eduardo Fuentes" style={S} /></div>
            <div><label style={L}>Email contacto OC</label><input value={form.oc_contact_email} onChange={e=>setForm({...form, oc_contact_email:e.target.value})} style={S} /></div>
          </div>
          {(form.billing_type==="B"||form.billing_type==="C"||form.billing_type==="D") && <div><label style={L}>Anticipación OC <span style={{fontWeight:400,color:"#9ca3af"}}>(días hábiles)</span></label><input type="number" min="1" max="60" value={form.oc_request_days} onChange={e=>setForm({...form, oc_request_days:parseInt(e.target.value)||15})} style={S} /></div>}
          {(form.billing_type==="C"||form.billing_type==="D") && <div><label style={L}>Anticipación HES/NdP <span style={{fontWeight:400,color:"#9ca3af"}}>(días hábiles)</span></label><input type="number" min="1" max="30" value={form.hes_request_days} onChange={e=>setForm({...form, hes_request_days:parseInt(e.target.value)||5})} style={S} /></div>}
        </div>
        <div style={{ marginBottom: 24 }}><label style={L}>Notas</label><textarea value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} rows={3} style={{...S, resize:"vertical"}} /></div>
        <div style={{ display: "flex", gap: 12 }}>
          <button type="submit" style={{ background: "#6366f1", color: "#fff", border: "none", borderRadius: 10, padding: "12px 32px", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>{isEdit ? "Guardar Cambios" : "Crear Cliente"}</button>
          <button type="button" onClick={onCancel} style={{ background: "#fff", color: "#6b7280", border: "1px solid #e5e7eb", borderRadius: 10, padding: "12px 32px", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>Cancelar</button>
        </div>
      </form>
    </div>
  );
}

function ClientDetail({ client, invoices, navTo, showToast, onRefresh, loadInvoices }) {
  const [documents, setDocuments] = useState([]);
  const [showDocForm, setShowDocForm] = useState(false);
  const [docForm, setDocForm] = useState({ year_month: "" });
  const [docFormError, setDocFormError] = useState("");
  const [currentClient, setCurrentClient] = useState(client);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editDocForm, setEditDocForm] = useState({ doc_number:"", doc_date:"", glosa:"", pdf_key:"", oc_currency:"UF", oc_net_clp:"", oc_total_clp:"", oc_net_usd:"", oc_total_usd:"" });
  const [showBillForm, setShowBillForm] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualForm, setManualForm] = useState({ items:[{nombre:"",precio:""}], sii_number:"", oc_number:"", hes_number:"", ndp_number:"", issue_date:"", service_month:"", service_year:"" });
  const todayISO = () => new Date().toISOString().split("T")[0];
  const nowMonth = () => String(new Date().getMonth() + 1).padStart(2,"0");
  const nowYear  = () => String(new Date().getFullYear());
  const [billForm, setBillForm] = useState({ items: [{ nombre: "", precio: "" }], glosa: "", oc_number: "", oc_date: "", hes_number: "", hes_date: "", ndp_number: "", ndp_date: "", nota: "", issue_date: todayISO(), service_month: nowMonth(), service_year: nowYear() });
  const [emittingBill, setEmittingBill] = useState(false);
  const billSectionRef = useRef(null);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const yearOptions = Array.from({ length: currentYear - 2022 }, (_, i) => currentYear - i);
  const loadDocs = useCallback(async () => { const res = await api(`/clients/${client.client_id}/documents`); if (res.success) setDocuments(res.data || []); }, [client.client_id]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const changeStatus = async (newStatus) => {
    const label = STATUS_OPTIONS.find(s=>s.value===newStatus)?.label || newStatus;
    if (!window.confirm(`¿Cambiar estado de "${currentClient.client_name}" a "${label}"?`)) return;
    const res = await api(`/clients/${client.client_id}`, "PUT", { status: newStatus });
    if (res.success) { showToast(`Estado: ${label}`); setCurrentClient(res.data || {...currentClient, status: newStatus}); if (onRefresh) onRefresh(); }
    else showToast(res.error, "error");
  };
  const deleteClient = async () => {
    if (!window.confirm(`⚠️ ¿ELIMINAR "${currentClient.client_name}"?\nEsta acción no se puede deshacer.`)) return;
    if (!window.confirm("¿CONFIRMAR eliminación?")) return;
    const res = await api(`/clients/${client.client_id}`, "DELETE");
    if (res.success) { showToast("Cliente eliminado"); if (onRefresh) onRefresh(); navTo("clients"); }
    else showToast(res.error, "error");
  };
  const deletedMonths = currentClient.deleted_months || [];
  const addedMonths   = currentClient.added_months   || [];

  const saveDoc = async () => {
    setDocFormError("");
    if (!docForm.year_month) { setDocFormError("Selecciona un mes."); return; }
    if (isMonthVisible(docForm.year_month)) { setDocFormError("Mes ya está creado."); return; }
    if (deletedMonths.includes(docForm.year_month)) {
      // Restaurar mes eliminado
      const newDeleted = deletedMonths.filter(ym => ym !== docForm.year_month);
      const res = await api(`/clients/${client.client_id}`, "PUT", { deleted_months: newDeleted });
      if (res.success) { setCurrentClient(res.data || {...currentClient, deleted_months: newDeleted}); showToast("Mes restaurado"); setShowDocForm(false); setDocForm({ year_month: "" }); }
      else showToast(res.error, "error");
    } else {
      // Forzar agregar mes (fuera del período activo o antes de primera factura)
      const newAdded = [...addedMonths, docForm.year_month];
      const res = await api(`/clients/${client.client_id}`, "PUT", { added_months: newAdded });
      if (res.success) { setCurrentClient(res.data || {...currentClient, added_months: newAdded}); showToast("Mes agregado"); setShowDocForm(false); setDocForm({ year_month: "" }); }
      else showToast(res.error, "error");
    }
  };

  const deleteMonth = async (ym, monthDocs) => {
    const monthName = MONTHS[parseInt(ym.split("-")[1]) - 1];
    if (!window.confirm(`¿Eliminar ${monthName} ${ym.split("-")[0]}?`)) return;
    for (const doc of monthDocs.filter(Boolean)) {
      await api(`/clients/${client.client_id}/documents/${ym}/${doc.doc_type}`, "DELETE");
    }
    const newDeleted = [...deletedMonths, ym];
    const newAdded   = addedMonths.filter(m => m !== ym);
    const res = await api(`/clients/${client.client_id}`, "PUT", { deleted_months: newDeleted, added_months: newAdded });
    if (res.success) {
      setCurrentClient(res.data || {...currentClient, deleted_months: newDeleted, added_months: newAdded});
      loadDocs();
    } else showToast(res.error, "error");
  };

  const openEditDoc = (ym, docType, existingDoc) => {
    setEditingDoc({ ym, docType, docKey: existingDoc?.doc_key || "" });
    setEditDocForm({
      doc_number: existingDoc?.doc_number || "",
      doc_date:   existingDoc?.doc_date   || "",
      glosa:      existingDoc?.glosa      || "",
      pdf_key:    existingDoc?.pdf_key    || "",
      oc_currency: existingDoc?.oc_currency || (currentClient.invoice_class === "exportacion" ? "USD" : "UF"),
      oc_net_clp:  existingDoc?.oc_net_clp  != null ? String(existingDoc.oc_net_clp)  : "",
      oc_total_clp:existingDoc?.oc_total_clp!= null ? String(existingDoc.oc_total_clp): "",
      oc_net_usd:  existingDoc?.oc_net_usd  != null ? String(existingDoc.oc_net_usd)  : "",
      oc_total_usd:existingDoc?.oc_total_usd!= null ? String(existingDoc.oc_total_usd): "",
    });
  };

  const saveDocInline = async () => {
    if (!editingDoc) return;
    const { ym, docType } = editingDoc;
    const payload = {
      year_month: ym, doc_type: docType,
      doc_number: editDocForm.doc_number,
      doc_date:   editDocForm.doc_date,
      glosa:      editDocForm.glosa,
      pdf_key:    editDocForm.pdf_key,
    };
    if (docType === "oc") {
      const cur = ["CLP","UF","USD"].includes(editDocForm.oc_currency) ? editDocForm.oc_currency : "UF";
      payload.oc_currency = cur;
      payload.oc_net_clp   = cur === "CLP" && editDocForm.oc_net_clp   !== "" ? parseInt(editDocForm.oc_net_clp,10)   : 0;
      payload.oc_total_clp = cur === "CLP" && editDocForm.oc_total_clp !== "" ? parseInt(editDocForm.oc_total_clp,10) : 0;
      payload.oc_net_usd   = cur === "USD" && editDocForm.oc_net_usd   !== "" ? parseFloat(editDocForm.oc_net_usd)   : 0;
      payload.oc_total_usd = cur === "USD" && editDocForm.oc_total_usd !== "" ? parseFloat(editDocForm.oc_total_usd) : 0;
    }
    const res = await api(`/clients/${client.client_id}/documents`, "POST", payload);
    if (res.success) {
      showToast("Documento guardado");
      setEditingDoc(null);
      loadDocs();
    } else showToast(res.error, "error");
  };

  const deleteDocInline = async () => {
    if (!editingDoc || !editingDoc.docKey) { setEditingDoc(null); return; }
    const label = editingDoc.docType === "oc" ? "OC" : editingDoc.docType === "hes" ? "HES" : "NdP";
    if (!window.confirm(`¿Quitar la ${label} de este mes? El mes quedará sin ${label} asignada.`)) return;
    const res = await api(`/clients/${client.client_id}/documents/${editingDoc.ym}/${editingDoc.docType}`, "DELETE");
    if (res.success) {
      showToast(`${label} quitada`);
      setEditingDoc(null);
      loadDocs();
    } else showToast(res.error, "error");
  };

  const billingType = BILLING_TYPES[currentClient.billing_type];
  const currentStatus = STATUS_OPTIONS.find(s=>s.value===currentClient.status) || STATUS_OPTIONS[0];
  const IS = { width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14, outline: "none", background: "#fff" };

  // Primer mes con factura (determina desde cuándo mostrar filas)
  const clientInvoices = (invoices || []).filter(i => i.client_id === client.client_id);
  const firstInvoiceYM = clientInvoices.length > 0
    ? [...clientInvoices].sort((a,b)=>(a.issue_date||"").localeCompare(b.issue_date||""))[0].issue_date?.substring(0,7)
    : (currentClient.created_at||"").substring(0,7); // Sin facturas → desde mes de creación

  // Historial de estados (para saber qué meses mostrar)
  // El período "activo desde siempre" arranca desde la primera factura, no desde created_at
  const getStatusHistory = () => {
    const h = currentClient.status_history;
    if (h && h.length > 0) return [...h].sort((a,b)=>a.date.localeCompare(b.date));
    return [{ status: "active", date: firstInvoiceYM || "2023-01" }];
  };

  // Un mes es visible si cae dentro de un período activo o es el mes de transición a inactivo/pausado
  // y no ha sido eliminado manualmente
  const isMonthVisible = (ym) => {
    if (deletedMonths.includes(ym)) return false;
    if (addedMonths.includes(ym)) return true;
    if (!firstInvoiceYM || ym < firstInvoiceYM) return false;
    const history = getStatusHistory();
    const applicable = history.filter(h => h.date <= ym).slice(-1)[0];
    if (!applicable) return false;
    if (applicable.status === "active") return true;
    return applicable.date === ym;
  };

  const monthlyData = MONTHS.map((name, i) => {
    const ym = `${selectedYear}-${String(i+1).padStart(2,"0")}`;
    if (!isMonthVisible(ym)) return null;
    const md = documents.filter(d => d.year_month === ym);
    return { name, ym, oc: md.find(d=>d.doc_type==="oc"), hes: md.find(d=>d.doc_type==="hes"), ndp: md.find(d=>d.doc_type==="ndp") };
  }).filter(Boolean);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        <button onClick={()=>navTo("clients")} style={{ background:"none", border:"1px solid #e5e7eb", borderRadius:8, padding:"8px 14px", cursor:"pointer", fontSize:13, color:"#6b7280" }}>← Volver</button>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a1d29", margin: 0 }}>{currentClient.client_name}</h2>
        <span style={{ padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: currentStatus.bg, color: currentStatus.color }}>{currentStatus.label}</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button onClick={()=>navTo("edit_client",currentClient)} style={{ background:"#6366f1", color:"#fff", border:"none", borderRadius:8, padding:"8px 16px", fontSize:13, fontWeight:600, cursor:"pointer" }}>Editar</button>
          <select value={currentClient.status} onChange={e=>changeStatus(e.target.value)} style={{ padding:"8px 12px", borderRadius:8, border:"1px solid #e5e7eb", fontSize:13, cursor:"pointer", background:"#fff", color:"#374151" }}>
            {STATUS_OPTIONS.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button onClick={deleteClient} style={{ background:"none", border:"1px solid #fee2e2", borderRadius:8, padding:"8px 14px", fontSize:13, cursor:"pointer", color:"#ef4444" }}>🗑️ Eliminar</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 28 }}>
        {[{ l:"RUT", v:currentClient.rut||"—" },{ l:"Razón social", v:currentClient.razon_social||currentClient.client_name||"—" },{ l:"Vertical", v:currentClient.vertical||"—" },{ l:"Día Facturación", v:`Día ${currentClient.billing_day}` },{ l:"Clasificación", v: currentClient.invoice_class === "exportacion" ? `Exportación${(COUNTRIES.find(c=>c.code===Number(currentClient.pais_receptor))||{}).name ? " — "+(COUNTRIES.find(c=>c.code===Number(currentClient.pais_receptor))).name : ""}` : "Nacional" },{ l:"Monto", v: currentClient.invoice_class === "exportacion" ? `US$${Number(currentClient.amount_usd||0).toLocaleString("en-US")}` : currentClient.billing_currency === "CLP" ? `$${Number(currentClient.amount_clp||0).toLocaleString("es-CL")} CLP` : `${currentClient.amount_uf} UF` },{ l:"Tipo", v:`${currentClient.billing_type} — ${billingType.name}` },{ l:"Pago", v:`${currentClient.payment_terms} días` },{ l:"Cotización", v:currentClient.quotation_id||"—" }].map((item,i)=>(
          <div key={i} style={{ background:"#fff", borderRadius:10, padding:"14px 18px", boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}><p style={{ fontSize:11, color:"#6b7280", margin:0, textTransform:"uppercase", letterSpacing:0.5 }}>{item.l}</p><p style={{ fontSize:15, fontWeight:600, color:"#1a1d29", margin:"4px 0 0" }}>{item.v}</p></div>
        ))}
      </div>

      <div style={{ background:"#fff", borderRadius:14, padding:24, marginBottom:28, boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
        <h3 style={{ fontSize:15, fontWeight:600, margin:"0 0 12px", color:"#1a1d29" }}>📧 Correos</h3>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, fontSize:14 }}>
          <div><span style={{ fontSize:12, color:"#6b7280" }}>Factura:</span><br/>{currentClient.invoice_emails?.join(", ")||"—"}</div>
          <div><span style={{ fontSize:12, color:"#6b7280" }}>Cobranza:</span><br/>{currentClient.collection_emails?.length ? currentClient.collection_emails.join(", ") : <span style={{color:"#f59e0b",fontSize:13}}>⚠️ Sin configurar</span>}</div>
          <div><span style={{ fontSize:12, color:"#6b7280" }}>HES/NdP:</span><br/>{currentClient.hes_contact_email||"—"}</div>
          <div>
            <span style={{ fontSize:12, color:"#6b7280" }}>Contacto OC:</span><br/>
            {currentClient.oc_contact_name && <span style={{fontWeight:500}}>{currentClient.oc_contact_name} — </span>}
            {currentClient.oc_contact_email||"—"}
          </div>
          {(currentClient.billing_type==="B"||currentClient.billing_type==="C"||currentClient.billing_type==="D") && <div><span style={{ fontSize:12, color:"#6b7280" }}>Anticipación OC:</span><br/>{currentClient.oc_request_days ?? 15} días hábiles</div>}
          {(currentClient.billing_type==="C"||currentClient.billing_type==="D") && <div><span style={{ fontSize:12, color:"#6b7280" }}>Anticipación HES/NdP:</span><br/>{currentClient.hes_request_days ?? 5} días hábiles</div>}
          {currentClient.modulo && <div><span style={{ fontSize:12, color:"#6b7280" }}>Módulo:</span><br/>{currentClient.modulo}</div>}
          {currentClient.procesos && String(currentClient.procesos) !== "0" && <div><span style={{ fontSize:12, color:"#6b7280" }}>Procesos:</span><br/>{currentClient.procesos}</div>}
        </div>
        {(currentClient.billing_type==="B"||currentClient.billing_type==="C"||currentClient.billing_type==="D") && (
          <div style={{ marginTop:16, paddingTop:16, borderTop:"1px solid #f3f4f6" }}>
            <button
              onClick={() => {
                const apiBase = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
                const token = sessionStorage.getItem("egenya_token");
                fetch(`${apiBase}/clients/${currentClient.client_id}/cotizacion`, {
                  headers: { Authorization: `Bearer ${token}` }
                }).then(r => r.blob()).then(blob => {
                  const url = URL.createObjectURL(blob);
                  window.open(url, "_blank");
                }).catch(() => alert("Error al generar cotización"));
              }}
              style={{ background:"#6366f1", color:"#fff", border:"none", borderRadius:8, padding:"8px 16px", fontSize:13, fontWeight:600, cursor:"pointer" }}
            >📄 Ver Cotización</button>
          </div>
        )}
      </div>

      {/* FACTURACIÓN LIBRE */}
      <div ref={billSectionRef} style={{ background:"#fff", borderRadius:14, padding:24, marginBottom:28, boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <h3 style={{ fontSize:15, fontWeight:600, margin:0, color:"#1a1d29" }}>🧾 Facturación</h3>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={()=>{ setShowBillForm(true); setShowManualForm(false); setBillForm({ items:[{ nombre:"", precio:"" }], glosa:currentClient.glosa||"", oc_number:"", oc_date:"", hes_number:"", hes_date:"", ndp_number:"", ndp_date:"", nota:"", issue_date:todayISO(), service_month:nowMonth(), service_year:nowYear() }); }}
              style={{ background:"#10b981", color:"#fff", border:"none", borderRadius:8, padding:"8px 16px", fontSize:13, fontWeight:600, cursor:"pointer" }}>+ Nueva Factura</button>
            <button onClick={()=>{ setShowManualForm(true); setShowBillForm(false); setManualForm({ items:[{nombre:"",precio:""}], sii_number:"", oc_number:"", hes_number:"", ndp_number:"", issue_date:todayISO(), service_month:nowMonth(), service_year:nowYear() }); }}
              style={{ background:"none", color:"#6b7280", border:"1px solid #e5e7eb", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:500, cursor:"pointer" }}>Registrar manualmente</button>
          </div>
        </div>
        {showManualForm && (()=>{
          const mTotalNeto = manualForm.items.reduce((s,it)=>s+(parseInt(it.precio)||0),0);
          const mTotalIVA = Math.round(mTotalNeto*0.19);
          const mTotalFinal = mTotalNeto+mTotalIVA;
          const mValido = manualForm.sii_number.trim() && manualForm.items.every(it=>it.nombre.trim()&&parseInt(it.precio)>0);
          return (
          <div style={{ marginTop:16, padding:20, background:"#fafafa", borderRadius:10, border:"1px dashed #d1d5db" }}>
            <p style={{ fontSize:12, color:"#6b7280", margin:"0 0 4px" }}>⚠️ Uso excepcional — para registrar una factura ya emitida desde el portal de SimpleFactura.</p>
            <p style={{ fontSize:13, fontWeight:600, color:"#374151", margin:"0 0 14px" }}>Registrar factura manual para <strong>{currentClient.client_name}</strong></p>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:14, background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8, padding:"12px 16px" }}>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:"#1d4ed8", display:"block", marginBottom:4 }}>📅 Fecha de factura *</label>
                <input type="date" value={manualForm.issue_date} onChange={e=>setManualForm({...manualForm,issue_date:e.target.value})} style={{ width:"100%", padding:"8px 12px", borderRadius:7, border:"1px solid #bfdbfe", fontSize:13, outline:"none", background:"#fff" }} />
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:"#1d4ed8", display:"block", marginBottom:4 }}>📋 Mes de servicio</label>
                <select value={manualForm.service_month} onChange={e=>setManualForm({...manualForm,service_month:e.target.value})} style={{ width:"100%", padding:"8px 12px", borderRadius:7, border:"1px solid #bfdbfe", fontSize:13, outline:"none", background:"#fff" }}>
                  {MONTHS.map((mn,i)=><option key={i} value={String(i+1).padStart(2,"0")}>{mn}</option>)}
                  <option value="otro">Otro (no asignar a mes)</option>
                </select>
              </div>
              {manualForm.service_month !== "otro" && (
                <div>
                  <label style={{ fontSize:12, fontWeight:600, color:"#1d4ed8", display:"block", marginBottom:4 }}>Año de servicio</label>
                  <input type="number" value={manualForm.service_year} onChange={e=>setManualForm({...manualForm,service_year:e.target.value})} min="2020" max="2030" style={{ width:"100%", padding:"8px 12px", borderRadius:7, border:"1px solid #bfdbfe", fontSize:13, outline:"none", background:"#fff", fontFamily:"DM Mono" }} />
                </div>
              )}
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:6 }}>N° Factura SII *</label>
              <input value={manualForm.sii_number} onChange={e=>setManualForm({...manualForm,sii_number:e.target.value})} placeholder="Ej: 451"
                style={{ width:180, padding:"9px 12px", borderRadius:7, border:"1px solid #e5e7eb", fontSize:15, fontFamily:"DM Mono", fontWeight:600, outline:"none" }} />
            </div>
            <p style={{ fontSize:12, fontWeight:600, color:"#374151", margin:"0 0 8px" }}>Ítems *</p>
            {manualForm.items.map((item,idx)=>(
              <div key={idx} style={{ display:"grid", gridTemplateColumns:"1fr 180px 32px", gap:8, marginBottom:8, alignItems:"center" }}>
                <input value={item.nombre} onChange={e=>{ const its=[...manualForm.items]; its[idx]={...its[idx],nombre:e.target.value}; setManualForm({...manualForm,items:its}); }} placeholder={`Descripción ítem ${idx+1}`}
                  style={{ padding:"9px 12px", borderRadius:7, border:"1px solid #e5e7eb", fontSize:13, outline:"none" }} />
                <input type="number" value={item.precio} onChange={e=>{ const its=[...manualForm.items]; its[idx]={...its[idx],precio:e.target.value}; setManualForm({...manualForm,items:its}); }} placeholder="Monto neto"
                  style={{ padding:"9px 12px", borderRadius:7, border:"1px solid #e5e7eb", fontSize:13, outline:"none", fontFamily:"DM Mono" }} />
                {manualForm.items.length>1
                  ? <button onClick={()=>{ const its=manualForm.items.filter((_,i)=>i!==idx); setManualForm({...manualForm,items:its}); }} style={{ background:"none", border:"none", color:"#ef4444", fontSize:18, cursor:"pointer", padding:0 }}>×</button>
                  : <span/>}
              </div>
            ))}
            <button onClick={()=>setManualForm({...manualForm,items:[...manualForm.items,{nombre:"",precio:""}]})}
              style={{ background:"none", border:"1px dashed #9ca3af", color:"#6b7280", borderRadius:7, padding:"6px 14px", fontSize:12, cursor:"pointer", marginBottom:14 }}>+ Agregar ítem</button>
            {mTotalNeto > 0 && (
              <div style={{ background:"#f0f4ff", borderRadius:8, padding:12, marginBottom:12 }}>
                <div style={{ display:"flex", gap:24, fontSize:14, fontFamily:"DM Mono" }}>
                  <span>Neto: <strong>${mTotalNeto.toLocaleString("es-CL")}</strong></span>
                  <span>IVA 19%: <strong>${mTotalIVA.toLocaleString("es-CL")}</strong></span>
                  <span>Total: <strong style={{ color:"#6366f1", fontSize:16 }}>${mTotalFinal.toLocaleString("es-CL")}</strong></span>
                </div>
              </div>
            )}
            <div style={{ display:"flex", gap:8 }}>
              <button disabled={!mValido} onClick={async ()=>{
                const issueDate = manualForm.issue_date || todayISO();
                const dueDate = new Date(issueDate); dueDate.setDate(dueDate.getDate()+(currentClient.payment_terms||30));
                const serviceYM = manualForm.service_month === "otro" ? "" : `${manualForm.service_year}-${manualForm.service_month}`;
                const items = manualForm.items.map(it=>({ nombre:it.nombre.trim(), cantidad:1, precio:parseInt(it.precio) }));
                const glosa = items.map(it=>it.nombre).join(" / ");
                const res = await api("/billing/emit", "POST", {
                  client_id: currentClient.client_id, sii_number: manualForm.sii_number.trim(),
                  issue_date: issueDate, due_date: dueDate.toISOString().split("T")[0],
                  net_clp: mTotalNeto, iva_clp: mTotalIVA, total_clp: mTotalFinal,
                  glosa, items, payment_terms: currentClient.payment_terms||30,
                  oc_number: manualForm.oc_number, hes_number: manualForm.hes_number,
                  year_month: serviceYM, invoice_type: "custom",
                });
                if (res.success) {
                  showToast(`✅ Factura N° ${manualForm.sii_number} registrada`);
                  setShowManualForm(false);
                  if (loadInvoices) loadInvoices();
                } else { alert(res.error || "Error al registrar"); }
              }} style={{ background:"#6b7280", color:"#fff", border:"none", borderRadius:8, padding:"10px 20px", fontSize:14, fontWeight:600, cursor:"pointer", opacity:mValido?1:0.5 }}>
                Registrar
              </button>
              <button onClick={()=>setShowManualForm(false)} style={{ background:"none", border:"1px solid #e5e7eb", borderRadius:8, padding:"10px 20px", fontSize:14, cursor:"pointer", color:"#6b7280" }}>Cancelar</button>
            </div>
          </div>
          );
        })()}
        {showBillForm && (()=>{
          const isExport = currentClient.invoice_class === "exportacion";
          const amountUsd = parseFloat(currentClient.amount_usd) || 0;
          const missingDTE = !currentClient.giro || !currentClient.direccion || !currentClient.comuna || !currentClient.ciudad;
          const totalNeto = billForm.items.reduce((s,it)=>s+(parseInt(it.precio)||0),0);
          const totalIVA = Math.round(totalNeto*0.19);
          const totalFinal = totalNeto+totalIVA;
          const itemsValidos = isExport ? (amountUsd > 0) : billForm.items.every(it=>it.nombre.trim() && parseInt(it.precio)>0);
          return (
          <div style={{ marginTop:16, padding:20, background:"#f8f9fb", borderRadius:10 }}>
            <p style={{ fontSize:13, color:"#6b7280", margin:"0 0 12px" }}>Emitir factura para <strong>{currentClient.client_name}</strong></p>
            {missingDTE && (
              <div style={{ background:"#fef3c7", border:"1px solid #f59e0b", borderRadius:8, padding:"10px 14px", marginBottom:14, fontSize:13, color:"#92400e" }}>
                ⚠️ Faltan datos del cliente requeridos para facturar: {[!currentClient.giro&&"Giro",!currentClient.direccion&&"Dirección",!currentClient.comuna&&"Comuna",!currentClient.ciudad&&"Ciudad"].filter(Boolean).join(", ")}. Edita el cliente primero.
              </div>
            )}
            <div style={{ background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8, padding:"12px 16px", marginBottom:16, display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:"#1d4ed8", display:"block", marginBottom:4 }}>📅 Fecha de factura *</label>
                <input type="date" value={billForm.issue_date} onChange={e=>setBillForm({...billForm, issue_date:e.target.value})} style={{ width:"100%", padding:"8px 12px", borderRadius:7, border:"1px solid #bfdbfe", fontSize:13, outline:"none", background:"#fff" }} />
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:"#1d4ed8", display:"block", marginBottom:4 }}>📋 Mes de servicio</label>
                <select value={billForm.service_month} onChange={e=>setBillForm({...billForm, service_month:e.target.value})} style={{ width:"100%", padding:"8px 12px", borderRadius:7, border:"1px solid #bfdbfe", fontSize:13, outline:"none", background:"#fff" }}>
                  {MONTHS.map((mn,i)=><option key={i} value={String(i+1).padStart(2,"0")}>{mn}</option>)}
                  <option value="otro">Otro (no asignar a mes)</option>
                </select>
              </div>
              {billForm.service_month !== "otro" && (
                <div>
                  <label style={{ fontSize:12, fontWeight:600, color:"#1d4ed8", display:"block", marginBottom:4 }}>Año de servicio</label>
                  <input type="number" value={billForm.service_year} onChange={e=>setBillForm({...billForm, service_year:e.target.value})} min="2020" max="2030" style={{ width:"100%", padding:"8px 12px", borderRadius:7, border:"1px solid #bfdbfe", fontSize:13, outline:"none", background:"#fff", fontFamily:"DM Mono" }} />
                </div>
              )}
            </div>

            {/* EXPORTACIÓN (DTE 110): monto USD fijo de la ficha, exento, sin referencias */}
            {isExport && (
              <>
                <div style={{ background:"#f0f4ff", borderRadius:8, padding:16, marginBottom:14 }}>
                  <div style={{ fontSize:18, fontWeight:700, color:"#1a1d29" }}>
                    Monto: <span style={{ fontFamily:"DM Mono", color:"#6366f1" }}>US$ {amountUsd.toLocaleString("en-US",{minimumFractionDigits:2})}</span>
                    <span style={{ marginLeft:10, fontSize:12, fontWeight:600, color:"#16a34a" }}>EXENTO DE IVA</span>
                  </div>
                  <p style={{ margin:"8px 0 0", fontSize:12, color:"#6b7280" }}>
                    Monto fijo definido en la ficha del cliente. El equivalente en CLP (dólar observado del día) se registra automáticamente en la sección <em>OtraMoneda</em> del DTE 110. La exportación de servicios no lleva referencias OC/HES.
                  </p>
                  {amountUsd <= 0 && (
                    <p style={{ margin:"8px 0 0", fontSize:12, color:"#dc2626", fontWeight:600 }}>
                      ⚠️ El cliente no tiene Monto USD definido. Edita la ficha antes de facturar.
                    </p>
                  )}
                </div>
                <div style={{ marginBottom:14 }}>
                  <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Glosa (descripción del servicio)</label>
                  <input value={billForm.glosa} onChange={e=>setBillForm({...billForm, glosa:e.target.value})} placeholder="Ej: Servicio mensual eGenya"
                    style={{ width:"100%", padding:"9px 12px", borderRadius:7, border:"1px solid #e5e7eb", fontSize:13, outline:"none" }} />
                </div>
              </>
            )}

            {!isExport && (<>
            {/* GLOSA (solo tipo A) */}
            {currentClient.billing_type === "A" && (
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, fontWeight:600, color:"#374151", display:"block", marginBottom:4 }}>Glosa (descripción del servicio)</label>
                <input value={billForm.glosa} onChange={e=>setBillForm({...billForm, glosa:e.target.value})} placeholder="Ej: Servicio de energía enero 2026"
                  style={{ width:"100%", padding:"9px 12px", borderRadius:7, border:"1px solid #e5e7eb", fontSize:13, outline:"none" }} />
              </div>
            )}

            {/* ITEMS */}
            <p style={{ fontSize:12, fontWeight:600, color:"#374151", margin:"0 0 8px" }}>Ítems de la factura *</p>
            {billForm.items.map((item, idx)=>(
              <div key={idx} style={{ display:"grid", gridTemplateColumns:"1fr 180px 32px", gap:8, marginBottom:8, alignItems:"center" }}>
                <input value={item.nombre} onChange={e=>{ const its=[...billForm.items]; its[idx]={...its[idx],nombre:e.target.value}; setBillForm({...billForm,items:its}); }} placeholder={`Descripción ítem ${idx+1}`}
                  style={{ padding:"9px 12px", borderRadius:7, border:"1px solid #e5e7eb", fontSize:13, outline:"none" }} />
                <input type="number" value={item.precio} onChange={e=>{ const its=[...billForm.items]; its[idx]={...its[idx],precio:e.target.value}; setBillForm({...billForm,items:its}); }} placeholder="Monto neto"
                  style={{ padding:"9px 12px", borderRadius:7, border:"1px solid #e5e7eb", fontSize:13, outline:"none", fontFamily:"DM Mono" }} />
                {billForm.items.length>1
                  ? <button onClick={()=>{ const its=billForm.items.filter((_,i)=>i!==idx); setBillForm({...billForm,items:its}); }} style={{ background:"none", border:"none", color:"#ef4444", fontSize:18, cursor:"pointer", padding:0, lineHeight:1 }}>×</button>
                  : <span/>}
              </div>
            ))}
            <button onClick={()=>setBillForm({...billForm, items:[...billForm.items,{nombre:"",precio:""}]})}
              style={{ background:"none", border:"1px dashed #6366f1", color:"#6366f1", borderRadius:7, padding:"6px 14px", fontSize:12, fontWeight:600, cursor:"pointer", marginBottom:14 }}>+ Agregar ítem</button>

            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:12, color:"#6b7280", display:"block", marginBottom:4 }}>Nota al pie de página (opcional)</label>
              <input value={billForm.nota} onChange={e=>setBillForm({...billForm, nota:e.target.value})} placeholder="Ej: Servicio prestado según contrato N° 123"
                style={{ width:"100%", padding:"9px 12px", borderRadius:7, border:"1px solid #e5e7eb", fontSize:13, outline:"none" }} />
            </div>

            <p style={{ fontSize:12, fontWeight:600, color:"#6366f1", margin:"0 0 8px" }}>📎 Referencias (aparecen en la factura del SII)</p>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:12 }}>
              <div>
                <label style={{ fontSize:12, color:"#6b7280" }}>N° Orden de Compra</label>
                <input value={billForm.oc_number} onChange={e=>setBillForm({...billForm, oc_number:e.target.value})} placeholder="Ej: 4902554172"
                  style={{ width:"100%", padding:"10px 14px", borderRadius:8, border:"1px solid #e5e7eb", fontSize:14, outline:"none" }} />
              </div>
              <div>
                <label style={{ fontSize:12, color:"#6b7280" }}>Fecha OC</label>
                <input type="date" value={billForm.oc_date} onChange={e=>setBillForm({...billForm, oc_date:e.target.value})}
                  style={{ width:"100%", padding:"10px 14px", borderRadius:8, border:"1px solid #e5e7eb", fontSize:14, outline:"none" }} />
              </div>
              <div></div>
              <div>
                <label style={{ fontSize:12, color:"#6b7280" }}>N° HES</label>
                <input value={billForm.hes_number} onChange={e=>setBillForm({...billForm, hes_number:e.target.value})} placeholder="Ej: 1021337010"
                  style={{ width:"100%", padding:"10px 14px", borderRadius:8, border:"1px solid #e5e7eb", fontSize:14, outline:"none" }} />
              </div>
              <div>
                <label style={{ fontSize:12, color:"#6b7280" }}>Fecha HES</label>
                <input type="date" value={billForm.hes_date} onChange={e=>setBillForm({...billForm, hes_date:e.target.value})}
                  style={{ width:"100%", padding:"10px 14px", borderRadius:8, border:"1px solid #e5e7eb", fontSize:14, outline:"none" }} />
              </div>
              <div></div>
              <div>
                <label style={{ fontSize:12, color:"#6b7280" }}>N° Nota de Pedido</label>
                <input value={billForm.ndp_number} onChange={e=>setBillForm({...billForm, ndp_number:e.target.value})} placeholder="Ej: NP-2026-001"
                  style={{ width:"100%", padding:"10px 14px", borderRadius:8, border:"1px solid #e5e7eb", fontSize:14, outline:"none" }} />
              </div>
              <div>
                <label style={{ fontSize:12, color:"#6b7280" }}>Fecha NdP</label>
                <input type="date" value={billForm.ndp_date} onChange={e=>setBillForm({...billForm, ndp_date:e.target.value})}
                  style={{ width:"100%", padding:"10px 14px", borderRadius:8, border:"1px solid #e5e7eb", fontSize:14, outline:"none" }} />
              </div>
            </div>

            {totalNeto > 0 && (
              <div style={{ background:"#f0f4ff", borderRadius:8, padding:12, marginBottom:12 }}>
                <div style={{ display:"flex", gap:24, fontSize:14, fontFamily:"DM Mono" }}>
                  <span>Neto: <strong>${totalNeto.toLocaleString("es-CL")}</strong></span>
                  <span>IVA 19%: <strong>${totalIVA.toLocaleString("es-CL")}</strong></span>
                  <span>Total: <strong style={{ color:"#6366f1", fontSize:16 }}>${totalFinal.toLocaleString("es-CL")}</strong></span>
                </div>
              </div>
            )}
            </>)}
            <div style={{ display:"flex", gap:8 }}>
              <button disabled={emittingBill || missingDTE || !itemsValidos} onClick={async ()=>{
                setEmittingBill(true);
                const issueDate = billForm.issue_date || todayISO();
                const dueDate = new Date(issueDate); dueDate.setDate(dueDate.getDate()+(currentClient.payment_terms||30));
                const serviceYM = billForm.service_month === "otro" ? "" : `${billForm.service_year}-${billForm.service_month}`;
                let payload;
                if (isExport) {
                  // Exportación (DTE 110): monto USD de la ficha, exento, sin referencias OC/HES.
                  payload = {
                    client_id: currentClient.client_id, emit_sii: true, issue_date: issueDate,
                    due_date: dueDate.toISOString().split("T")[0],
                    glosa: billForm.glosa.trim() || "Servicio mensual eGenya",
                    amount_usd: amountUsd,
                    payment_terms: currentClient.payment_terms || 30,
                    year_month: serviceYM, invoice_type: "custom",
                  };
                } else {
                  const referencias = [];
                  if (billForm.oc_number) referencias.push({ tipo:"oc", folio:billForm.oc_number, fecha:billForm.oc_date });
                  if (billForm.hes_number) referencias.push({ tipo:"hes", folio:billForm.hes_number, fecha:billForm.hes_date });
                  if (billForm.ndp_number) referencias.push({ tipo:"ndp", folio:billForm.ndp_number, fecha:billForm.ndp_date });
                  const items = billForm.items.map(it=>({ nombre:it.nombre.trim(), cantidad:1, precio:parseInt(it.precio) }));
                  if (billForm.nota.trim()) items.push({ nombre: billForm.nota.trim(), cantidad: 1, precio: 0 });
                  const glosa = (currentClient.billing_type === "A" && billForm.glosa.trim()) ? billForm.glosa.trim() : billForm.items.map(it=>it.nombre.trim()).join(" / ");
                  payload = {
                    client_id: currentClient.client_id, emit_sii: true, issue_date: issueDate,
                    due_date: dueDate.toISOString().split("T")[0], net_clp: totalNeto, iva_clp: totalIVA, total_clp: totalFinal,
                    glosa, items, payment_terms: currentClient.payment_terms || 30,
                    oc_number: billForm.oc_number, hes_number: billForm.hes_number,
                    year_month: serviceYM, invoice_type: "custom",
                    referencias,
                  };
                }
                const res = await api("/billing/emit", "POST", payload);
                if (res.success) {
                  showToast(`✅ Factura N° ${res.data?.sii_number} emitida`);
                  setShowBillForm(false);
                  if (loadInvoices) loadInvoices();
                  if (currentClient.invoice_emails?.length && res.data?.invoice_id) {
                    if (window.confirm(`¿Enviar correo de factura a ${currentClient.invoice_emails.join(", ")}?`)) {
                      const emailRes = await api(`/invoices/${res.data.invoice_id}/send-email`, "POST", {});
                      if (emailRes.success) showToast(`📧 Correo enviado (${emailRes.attachments_count} adjuntos${emailRes.attachment_errors?.length ? " ⚠️ " + emailRes.attachment_errors.join(" | ") : ""})`);
                      else showToast(emailRes.error || "Error enviando correo", "error");
                    }
                  }
                } else { alert(res.error || "Error al facturar"); }
                setEmittingBill(false);
              }} style={{ background:"#10b981", color:"#fff", border:"none", borderRadius:8, padding:"10px 20px", fontSize:14, fontWeight:600, cursor:"pointer", opacity:(emittingBill||missingDTE||!itemsValidos)?0.5:1 }}>
                {emittingBill ? "Facturando..." : "🧾 Facturar"}
              </button>
              <button onClick={()=>setShowBillForm(false)} style={{ background:"none", border:"1px solid #e5e7eb", borderRadius:8, padding:"10px 20px", fontSize:14, cursor:"pointer", color:"#6b7280" }}>Cancelar</button>
            </div>
          </div>
          );
        })()}
      </div>

      <div style={{ background:"#fff", borderRadius:14, padding:24, boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <h3 style={{ fontSize:15, fontWeight:600, margin:0, color:"#1a1d29" }}>📋 Documentos Mensuales</h3>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <select value={selectedYear} onChange={e=>setSelectedYear(Number(e.target.value))} style={{ padding:"6px 12px", borderRadius:8, border:"1px solid #e5e7eb", fontSize:13, fontWeight:600, color:"#374151", background:"#fff", cursor:"pointer" }}>
                {yearOptions.map(y=><option key={y} value={y}>{y}</option>)}
              </select>
              <button onClick={()=>setShowDocForm(true)} style={{ background:"#6366f1", color:"#fff", border:"none", borderRadius:8, padding:"8px 16px", fontSize:13, fontWeight:600, cursor:"pointer" }}>+ Agregar</button>
            </div>
          </div>
          {showDocForm && (
            <div style={{ padding:20, background:"#f8f9fb", borderRadius:10, marginBottom:16 }}>
              <p style={{ fontSize:12, color:"#6b7280", margin:"0 0 10px" }}>Agregar o restaurar una fila mensual.</p>
              <div style={{ marginBottom:12 }}>
                <label style={{ fontSize:12, fontWeight:600, color:"#374151" }}>Mes</label>
                <select value={docForm.year_month} onChange={e=>{setDocForm({year_month:e.target.value});setDocFormError("");}} style={IS}>
                  <option value="">Seleccionar...</option>
                  {MONTHS.map((mn,i)=><option key={i} value={`${selectedYear}-${String(i+1).padStart(2,"0")}`}>{mn} {selectedYear}</option>)}
                </select>
              </div>
              {docFormError && <p style={{ color:"#ef4444", fontSize:13, margin:"0 0 8px", fontWeight:600 }}>{docFormError}</p>}
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={saveDoc} style={{ background:"#10b981", color:"#fff", border:"none", borderRadius:8, padding:"8px 20px", fontSize:13, fontWeight:600, cursor:"pointer" }}>Agregar</button>
                <button onClick={()=>{setShowDocForm(false);setDocFormError("");}} style={{ background:"none", border:"1px solid #e5e7eb", borderRadius:8, padding:"8px 20px", fontSize:13, cursor:"pointer", color:"#6b7280" }}>Cancelar</button>
              </div>
            </div>
          )}
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead><tr style={{ background:"#f8f9fb" }}>
              <th style={{ padding:"10px 14px", textAlign:"left", fontSize:12, fontWeight:600, color:"#6b7280" }}>Mes</th>
              {currentClient.billing_type !== "A" && <th style={{ padding:"10px 14px", textAlign:"left", fontSize:12, fontWeight:600, color:"#6b7280" }}>OC</th>}
              {billingType.requires_hes && <th style={{ padding:"10px 14px", textAlign:"left", fontSize:12, fontWeight:600, color:"#6b7280" }}>HES</th>}
              {billingType.requires_ndp && <th style={{ padding:"10px 14px", textAlign:"left", fontSize:12, fontWeight:600, color:"#6b7280" }}>NdP</th>}
              <th style={{ padding:"10px 14px", textAlign:"left", fontSize:12, fontWeight:600, color:"#6b7280" }}>Estado</th>
              <th style={{ padding:"10px 14px", textAlign:"left", fontSize:12, fontWeight:600, color:"#6b7280" }}>Factura</th>
              <th style={{ padding:"10px 14px", textAlign:"left", fontSize:12, fontWeight:600, color:"#6b7280" }}>Facturar</th>
              <th style={{ padding:"10px 14px", width:70 }}></th>
            </tr></thead>
            <tbody>
              {monthlyData.length === 0
                ? <tr><td colSpan={10} style={{padding:"32px 14px",textAlign:"center",color:"#9ca3af",fontSize:13}}>Sin actividad en {selectedYear}</td></tr>
                : monthlyData.map((m,i)=>{
                    const isTypeA = currentClient.billing_type === "A";
                    const hasOC=!!(m.oc && m.oc.doc_number);
                    const ready=isTypeA||(hasOC&&(!billingType.requires_hes||!!(m.hes&&m.hes.doc_number))&&(!billingType.requires_ndp||!!(m.ndp&&m.ndp.doc_number)));
                    const DC=({doc,onClick})=>{if(!doc||!doc.doc_number)return<span onClick={onClick} style={{color:"#9ca3af",fontSize:12,cursor:"pointer",textDecoration:"underline dotted"}} title="Clic para agregar">Pendiente</span>;return(<span style={{display:"inline-flex",alignItems:"center",gap:6}}><span style={{color:"#065f46",background:"#d1fae5",padding:"3px 8px",borderRadius:4,fontSize:12}}>✓ {doc.doc_number}</span>{doc.pdf_key&&<button onClick={()=>downloadPDF(doc.pdf_key)} style={{background:"none",border:"none",cursor:"pointer",color:"#6366f1",fontSize:11,padding:0}}>📎</button>}<button onClick={onClick} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af",fontSize:11,padding:0}} title="Editar">✏️</button></span>);};
                    const hasInvoice = clientInvoices.some(inv => {
                      if (inv.payment_status === "con_nc") return false;
                      if (inv.year_month === "") return false; // service_month="Otro" → no se asigna a ningún mes
                      const ym = inv.year_month || (inv.issue_date||"").substring(0,7);
                      return ym === m.ym;
                    });
                    const [ymYear, ymMonth] = m.ym.split("-").map(Number);
                    const billingDate = new Date(ymYear, ymMonth - 1, currentClient.billing_day || 1);
                    const facturaStatus = hasInvoice
                      ? { label: "Facturado", bg: "#d1fae5", color: "#065f46" }
                      : new Date() > billingDate
                        ? { label: "Vencido", bg: "#fee2e2", color: "#991b1b" }
                        : { label: "Por Facturar", bg: "#fef3c7", color: "#92400e" };
                    const btnStyle = { background:"none", border:"1px solid #e5e7eb", borderRadius:6, padding:"3px 7px", fontSize:11, cursor:"pointer", lineHeight:1 };
                    return(
                      <React.Fragment key={i}>
                        <tr style={{borderTop:"1px solid #f3f4f6"}}>
                          <td style={{padding:"12px 14px",fontWeight:500,fontSize:13}}>{m.name}</td>
                          {!isTypeA&&<td style={{padding:"12px 14px",fontSize:13}}><DC doc={m.oc} onClick={()=>openEditDoc(m.ym,"oc",m.oc)}/>{m.oc?.oc_currency==="CLP"&&(Number(m.oc.oc_net_clp)||Number(m.oc.oc_total_clp))?<span title="OC en pesos — la factura usa este monto" style={{display:"block",marginTop:3,fontSize:11,color:"#9d174d",background:"#fce7f3",padding:"1px 6px",borderRadius:4,width:"fit-content",fontFamily:"DM Mono"}}>CLP ${Number(m.oc.oc_net_clp||Math.round(Number(m.oc.oc_total_clp)/1.19)).toLocaleString("es-CL")} neto</span>:m.oc?.oc_currency==="USD"&&(Number(m.oc.oc_net_usd)||Number(m.oc.oc_total_usd))?<span title="OC en dólares (referencial — la factura de exportación usa el Monto USD de la ficha)" style={{display:"block",marginTop:3,fontSize:11,color:"#1e40af",background:"#dbeafe",padding:"1px 6px",borderRadius:4,width:"fit-content",fontFamily:"DM Mono"}}>USD ${Number(m.oc.oc_net_usd||m.oc.oc_total_usd).toLocaleString("en-US",{minimumFractionDigits:2})} neto</span>:null}</td>}
                          {billingType.requires_hes&&<td style={{padding:"12px 14px",fontSize:13}}>{hasOC?<DC doc={m.hes} onClick={()=>openEditDoc(m.ym,"hes",m.hes)}/>:<span style={{color:"#d1d5db",fontSize:12}}>—</span>}</td>}
                          {billingType.requires_ndp&&<td style={{padding:"12px 14px",fontSize:13}}>{hasOC?<DC doc={m.ndp} onClick={()=>openEditDoc(m.ym,"ndp",m.ndp)}/>:<span style={{color:"#d1d5db",fontSize:12}}>—</span>}</td>}
                          <td style={{padding:"12px 14px"}}><span style={{padding:"3px 10px",borderRadius:4,fontSize:12,fontWeight:600,background:ready?"#d1fae5":"#fef3c7",color:ready?"#065f46":"#92400e"}}>{ready?"✅ Listo":"⏳ Pendiente"}</span></td>
                          <td style={{padding:"12px 14px"}}><span style={{padding:"3px 10px",borderRadius:4,fontSize:12,fontWeight:600,background:facturaStatus.bg,color:facturaStatus.color}}>{facturaStatus.label}</span></td>
                          <td style={{padding:"8px 14px"}}>
                            <button
                              disabled={!ready || hasInvoice}
                              title={hasInvoice ? "Ya facturado" : ready ? "Facturar este mes" : "Faltan documentos requeridos"}
                              onClick={async ()=>{
                                const docGlosa = m.oc?.glosa || m.hes?.glosa || m.ndp?.glosa || "";
                                const monthName = MONTHS[parseInt(m.ym.split("-")[1]) - 1];
                                const generatedGlosa = currentClient.billing_type === "A" ? (currentClient.glosa || `Servicio mantención mensual eGenya ${monthName}`) : docGlosa;
                                const defaultNombre = generatedGlosa;
                                const defaultGlosa = generatedGlosa;
                                setBillForm({
                                  items: [{ nombre: defaultNombre, precio: "" }],
                                  glosa: defaultGlosa,
                                  nota: "",
                                  oc_number:  m.oc?.doc_number  || "",
                                  oc_date:    m.oc?.doc_date    || "",
                                  hes_number: m.hes?.doc_number || "",
                                  hes_date:   m.hes?.doc_date   || "",
                                  ndp_number: m.ndp?.doc_number || "",
                                  ndp_date:   m.ndp?.doc_date   || "",
                                  issue_date: todayISO(),
                                  service_month: m.ym.split("-")[1],
                                  service_year:  m.ym.split("-")[0],
                                });
                                setShowBillForm(true);
                                setTimeout(()=>billSectionRef.current?.scrollIntoView({behavior:"smooth",block:"start"}), 50);
                                // Prioridad: si la OC del mes viene en CLP, el monto es el de la OC
                                const ocNet = (m.oc?.oc_currency === "CLP")
                                  ? (Number(m.oc.oc_net_clp) || (Number(m.oc.oc_total_clp) ? Math.round(Number(m.oc.oc_total_clp)/1.19) : 0))
                                  : 0;
                                if (ocNet) {
                                  setBillForm(prev => ({ ...prev, items: [{ ...prev.items[0], precio: String(ocNet) }] }));
                                } else if (currentClient.billing_currency === "CLP") {
                                  const precio = String(currentClient.amount_clp || 0);
                                  setBillForm(prev => ({ ...prev, items: [{ ...prev.items[0], precio }] }));
                                } else {
                                  try {
                                    const ufRes = await api("/uf");
                                    if (ufRes.success && ufRes.uf_value && currentClient.amount_uf) {
                                      const precio = String(Math.round(currentClient.amount_uf * ufRes.uf_value));
                                      setBillForm(prev => ({ ...prev, items: [{ ...prev.items[0], precio }] }));
                                    }
                                  } catch(e) {}
                                }
                              }}
                              style={{
                                background: hasInvoice ? "#d1fae5" : ready ? "#6366f1" : "#e5e7eb",
                                color: hasInvoice ? "#065f46" : ready ? "#fff" : "#9ca3af",
                                border: "none", borderRadius: 7,
                                padding: "5px 12px", fontSize: 12, fontWeight: 600,
                                cursor: (ready && !hasInvoice) ? "pointer" : "not-allowed",
                                whiteSpace: "nowrap",
                              }}>
                              {hasInvoice ? "✓ Facturado" : "💰 Facturar"}
                            </button>
                          </td>
                          <td style={{padding:"8px 10px"}}>
                            <div style={{display:"flex",gap:4,justifyContent:"flex-end"}}>
                              <button title="Eliminar mes" onClick={()=>deleteMonth(m.ym,[m.oc,m.hes,m.ndp])} style={{...btnStyle,color:"#ef4444",borderColor:"#fecaca"}}>✕</button>
                            </div>
                          </td>
                        </tr>
                        {editingDoc && editingDoc.ym === m.ym && (
                          <tr style={{background:"#f0f4ff"}}>
                            <td colSpan={10} style={{padding:"12px 16px"}}>
                              <div style={{display:"flex",gap:12,alignItems:"flex-end",flexWrap:"wrap"}}>
                                <div><label style={{fontSize:11,color:"#6b7280",display:"block",marginBottom:3}}>{editingDoc.docType==="oc"?"OC":editingDoc.docType==="hes"?"HES":"NdP"} N°</label><input value={editDocForm.doc_number} onChange={e=>setEditDocForm({...editDocForm,doc_number:e.target.value})} style={{padding:"6px 10px",borderRadius:6,border:"1px solid #c7d2fe",fontSize:13,width:130}} autoFocus /></div>
                                <div><label style={{fontSize:11,color:"#6b7280",display:"block",marginBottom:3}}>Fecha</label><input type="date" value={editDocForm.doc_date} onChange={e=>setEditDocForm({...editDocForm,doc_date:e.target.value})} style={{padding:"6px 10px",borderRadius:6,border:"1px solid #c7d2fe",fontSize:13}} /></div>
                                {editingDoc.docType==="oc"&&<div><label style={{fontSize:11,color:"#6b7280",display:"block",marginBottom:3}}>Glosa</label><input value={editDocForm.glosa} onChange={e=>setEditDocForm({...editDocForm,glosa:e.target.value})} placeholder="Descripción..." style={{padding:"6px 10px",borderRadius:6,border:"1px solid #c7d2fe",fontSize:13,width:190}} /></div>}
                                {editingDoc.docType==="oc"&&<div><label style={{fontSize:11,color:"#6b7280",display:"block",marginBottom:3}}>Moneda OC</label><select value={editDocForm.oc_currency} onChange={e=>setEditDocForm({...editDocForm,oc_currency:e.target.value})} style={{padding:"6px 10px",borderRadius:6,border:"1px solid #c7d2fe",fontSize:13}}><option value="UF">UF</option><option value="CLP">CLP</option><option value="USD">USD</option></select></div>}
                                {editingDoc.docType==="oc"&&editDocForm.oc_currency==="CLP"&&<div><label style={{fontSize:11,color:"#6b7280",display:"block",marginBottom:3}}>Neto CLP</label><input type="number" value={editDocForm.oc_net_clp} onChange={e=>setEditDocForm({...editDocForm,oc_net_clp:e.target.value})} placeholder="0" style={{padding:"6px 10px",borderRadius:6,border:"1px solid #c7d2fe",fontSize:13,width:120,fontFamily:"DM Mono"}} /></div>}
                                {editingDoc.docType==="oc"&&editDocForm.oc_currency==="CLP"&&<div><label style={{fontSize:11,color:"#6b7280",display:"block",marginBottom:3}}>Total CLP <span style={{color:"#9ca3af",fontWeight:400}}>(opc.)</span></label><input type="number" value={editDocForm.oc_total_clp} onChange={e=>setEditDocForm({...editDocForm,oc_total_clp:e.target.value})} placeholder="0" style={{padding:"6px 10px",borderRadius:6,border:"1px solid #c7d2fe",fontSize:13,width:120,fontFamily:"DM Mono"}} /></div>}
                                {editingDoc.docType==="oc"&&editDocForm.oc_currency==="USD"&&<div><label style={{fontSize:11,color:"#6b7280",display:"block",marginBottom:3}}>Neto USD</label><input type="number" step="0.01" value={editDocForm.oc_net_usd} onChange={e=>setEditDocForm({...editDocForm,oc_net_usd:e.target.value})} placeholder="0.00" style={{padding:"6px 10px",borderRadius:6,border:"1px solid #c7d2fe",fontSize:13,width:120,fontFamily:"DM Mono"}} /></div>}
                                {editingDoc.docType==="oc"&&editDocForm.oc_currency==="USD"&&<div><label style={{fontSize:11,color:"#6b7280",display:"block",marginBottom:3}}>Total USD <span style={{color:"#9ca3af",fontWeight:400}}>(opc.)</span></label><input type="number" step="0.01" value={editDocForm.oc_total_usd} onChange={e=>setEditDocForm({...editDocForm,oc_total_usd:e.target.value})} placeholder="0.00" style={{padding:"6px 10px",borderRadius:6,border:"1px solid #c7d2fe",fontSize:13,width:120,fontFamily:"DM Mono"}} /></div>}
                                <div><label style={{fontSize:11,color:"#6b7280",display:"block",marginBottom:3}}>PDF</label><PdfUploadButton clientId={client.client_id} docType={editingDoc.docType} yearMonth={editingDoc.ym} currentKey={editDocForm.pdf_key} onUploaded={(key)=>setEditDocForm(f=>({...f,pdf_key:key}))} onExtracted={editingDoc.docType==="oc"?(d)=>setEditDocForm(f=>({...f,doc_number:d.doc_number||f.doc_number,doc_date:d.doc_date||f.doc_date,glosa:d.glosa||f.glosa,oc_currency:d.currency||f.oc_currency,oc_net_clp:d.net_clp!=null?String(d.net_clp):f.oc_net_clp,oc_total_clp:d.total_clp!=null?String(d.total_clp):f.oc_total_clp,oc_net_usd:d.net_usd!=null?String(d.net_usd):f.oc_net_usd,oc_total_usd:d.total_usd!=null?String(d.total_usd):f.oc_total_usd})):undefined} showToast={showToast} /></div>
                                <div style={{display:"flex",gap:6}}>
                                  <button onClick={saveDocInline} style={{background:"#10b981",color:"#fff",border:"none",borderRadius:6,padding:"6px 14px",fontSize:12,fontWeight:600,cursor:"pointer"}}>Guardar</button>
                                  {editingDoc.docKey&&<button onClick={deleteDocInline} style={{background:"none",border:"1px solid #fecaca",borderRadius:6,padding:"6px 14px",fontSize:12,fontWeight:600,cursor:"pointer",color:"#ef4444"}}>Quitar {editingDoc.docType==="oc"?"OC":editingDoc.docType==="hes"?"HES":"NdP"}</button>}
                                  <button onClick={()=>setEditingDoc(null)} style={{background:"none",border:"1px solid #e5e7eb",borderRadius:6,padding:"6px 14px",fontSize:12,cursor:"pointer",color:"#6b7280"}}>Cancelar</button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
              }
            </tbody>
          </table>
        </div>
    </div>
  );
}

function InvoiceList({ invoices, clients, showToast, loadInvoices }) {
  const [sortCol, setSortCol] = useState("sii_number");
  const [sortDir, setSortDir] = useState("desc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingDateId, setEditingDateId] = useState(null);
  const [editingDateValue, setEditingDateValue] = useState("");
  const [toastLocal, setToastLocal] = useState(null);
  const [ncModal, setNcModal] = useState(null);
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");

  const handleExport = () => {
    try {
      const STATUS_MAP = { pending:"Pendiente", paid:"Pagada", overdue:"Vencida", collection:"Cobranza", con_nc:"Con NC" };
      const clientMap = {};
      clients.forEach(c => { clientMap[c.client_id] = c; });
      let filtered = [...invoices];
      if (exportFrom) filtered = filtered.filter(i => (i.issue_date||"") >= exportFrom);
      if (exportTo)   filtered = filtered.filter(i => (i.issue_date||"") <= exportTo);
      filtered.sort((a,b) => (a.issue_date||"") < (b.issue_date||"") ? -1 : 1);
      const rows = [
        ["Folio","Cliente","RUT","Vertical","Fecha Emisión","Mes Servicio","Neto CLP","IVA CLP","Total CLP","Estado"],
        ...filtered.map(i => {
          const c = clientMap[i.client_id] || {};
          return [
            i.sii_number||"", c.client_name||i.client_name||"", c.rut||i.rut||"", c.vertical||"", i.issue_date||"",
            i.year_month||"", i.net_clp||"", i.iva_clp||"", i.total_clp||"",
            STATUS_MAP[i.payment_status]||i.payment_status||""
          ];
        })
      ];
      const csv = "\ufeff" + rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(";")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `facturas_${exportFrom||"inicio"}_${exportTo||"hoy"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast("Error al exportar: " + e.message, "error");
    }
  };

  const getName = (id) => clients.find(c=>c.client_id===id)?.client_name||"—";
  const sc = { pending:{bg:"#fef3c7",color:"#92400e",label:"Pendiente"}, paid:{bg:"#d1fae5",color:"#065f46",label:"Pagada"}, overdue:{bg:"#fee2e2",color:"#991b1b",label:"Vencida"}, collection:{bg:"#fce7f3",color:"#9d174d",label:"En Cobranza"}, con_nc:{bg:"#f3f4f6",color:"#6b7280",label:"Con NC"} };
  const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  const fmtServiceMonth = (ym) => {
    if (!ym) return "Otro";
    const [y, m] = ym.split("-");
    return `${MONTHS[parseInt(m,10)-1]} ${y}`;
  };

  const availableYears = [...new Set(invoices.map(i => (i.issue_date||"").slice(0,4)).filter(Boolean))].sort((a,b)=>b-a);
  if (!availableYears.includes(yearFilter)) availableYears.unshift(yearFilter);

  const filtered = invoices.filter(i => {
    if ((i.issue_date||"").slice(0,4) !== yearFilter) return false;
    if (statusFilter !== "all" && i.payment_status !== statusFilter) return false;
    if (searchTerm) {
      const name = getName(i.client_id).toLowerCase();
      const sii = (i.sii_number || "").toLowerCase();
      const term = searchTerm.toLowerCase();
      if (!name.includes(term) && !sii.includes(term)) return false;
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    let va = a[sortCol], vb = b[sortCol];
    if (sortCol === "client_name") { va = getName(a.client_id); vb = getName(b.client_id); }
    if (sortCol === "net_clp" || sortCol === "total_clp") { va = Number(va || 0); vb = Number(vb || 0); }
    if (sortCol === "sii_number") { va = parseInt(va) || 0; vb = parseInt(vb) || 0; }
    if (va < vb) return sortDir === "asc" ? -1 : 1;
    if (va > vb) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  const changeStatus = async (invoiceId, newStatus) => {
    if (newStatus === "con_nc") {
      const inv = invoices.find(i => i.invoice_id === invoiceId);
      setNcModal(inv);
      setEditingId(null);
      return;
    }
    const res = await api(`/invoices/${invoiceId}`, "PUT", { payment_status: newStatus });
    if (res.success) {
      setToastLocal(`Estado actualizado a ${sc[newStatus]?.label || newStatus}`);
      setTimeout(() => setToastLocal(null), 3000);
      setEditingId(null);
      loadInvoices();
    }
  };

  const confirmNC = async () => {
    const res = await api(`/invoices/${ncModal.invoice_id}`, "PUT", { payment_status: "con_nc" });
    if (res.success) {
      setToastLocal("Factura marcada Con NC — el período vuelve a quedar pendiente");
      setTimeout(() => setToastLocal(null), 4000);
      setNcModal(null);
      loadInvoices();
    }
  };

  const saveDate = async (invoiceId, newDate, clientId) => {
    if (!newDate) { setEditingDateId(null); return; }
    const client = clients.find(c => c.client_id === clientId);
    const paymentTerms = parseInt(client?.payment_terms || 30);
    const due = new Date(newDate);
    due.setDate(due.getDate() + paymentTerms);
    const dueDateStr = due.toISOString().split("T")[0];
    const res = await api(`/invoices/${invoiceId}`, "PUT", { issue_date: newDate, due_date: dueDateStr });
    if (res.success) {
      setToastLocal("Fecha actualizada");
      setTimeout(() => setToastLocal(null), 3000);
      setEditingDateId(null);
      loadInvoices();
    } else {
      setToastLocal("Error: " + res.error);
      setTimeout(() => setToastLocal(null), 3000);
    }
  };

  const yearInvoices = invoices.filter(i => (i.issue_date||"").slice(0,4) === yearFilter);
  const counts = { all: yearInvoices.length, pending: yearInvoices.filter(i=>i.payment_status==="pending").length, paid: yearInvoices.filter(i=>i.payment_status==="paid").length, overdue: yearInvoices.filter(i=>i.payment_status==="overdue").length, collection: yearInvoices.filter(i=>i.payment_status==="collection").length, con_nc: yearInvoices.filter(i=>i.payment_status==="con_nc").length };
  const fmtCLP = (n) => "$" + Math.round(Number(n)||0).toLocaleString("es-CL");
  const sumBy = (st) => yearInvoices.filter(i=>i.payment_status===st).reduce((s,i)=>s+Number(i.total_clp||0),0);
  const amounts = { pending: sumBy("pending"), overdue: sumBy("overdue"), collection: sumBy("collection") };

  const SortHeader = ({ col, label }) => (
    <th onClick={() => toggleSort(col)} style={{ padding:"12px 16px", textAlign:"left", fontSize:12, fontWeight:600, color:"#6b7280", textTransform:"uppercase", letterSpacing:0.5, cursor:"pointer", userSelect:"none" }}>
      {label} {sortCol === col ? (sortDir === "asc" ? "▲" : "▼") : ""}
    </th>
  );

  return (
    <div>
      {toastLocal && <div style={{ position:"fixed",top:20,right:20,zIndex:1000,padding:"12px 24px",borderRadius:10,color:"#fff",fontSize:14,fontWeight:500,background:"#27ae60",boxShadow:"0 4px 20px rgba(0,0,0,0.15)" }}>{toastLocal}</div>}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <h2 style={{ fontSize:24, fontWeight:700, color:"#1a1d29", margin:0 }}>Facturas</h2>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:13, color:"#6b7280", fontWeight:500 }}>Exportar:</span>
          <input type="date" value={exportFrom} onChange={e=>setExportFrom(e.target.value)} style={{ padding:"6px 10px", borderRadius:8, border:"1px solid #e5e7eb", fontSize:13, outline:"none" }} />
          <span style={{ fontSize:13, color:"#6b7280" }}>—</span>
          <input type="date" value={exportTo} onChange={e=>setExportTo(e.target.value)} style={{ padding:"6px 10px", borderRadius:8, border:"1px solid #e5e7eb", fontSize:13, outline:"none" }} />
          <button onClick={handleExport} style={{ padding:"7px 16px", borderRadius:8, background:"#6366f1", color:"#fff", border:"none", fontSize:13, fontWeight:600, cursor:"pointer" }}>
            ⬇ Descargar CSV
          </button>
        </div>
      </div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:12 }}>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          {[{v:"all",l:"Todas",c:"#6366f1"},{v:"pending",l:"Pendientes",c:"#f59e0b"},{v:"paid",l:"Pagadas",c:"#10b981"},{v:"overdue",l:"Vencidas",c:"#ef4444"},{v:"collection",l:"En Cobranza",c:"#ec4899"},{v:"con_nc",l:"Con NC",c:"#6b7280"}].map(f=>(
            <button key={f.v} onClick={()=>setStatusFilter(f.v)} style={{ padding:"6px 14px", borderRadius:8, fontSize:13, fontWeight:statusFilter===f.v?600:400, border:statusFilter===f.v?`2px solid ${f.c}`:"1px solid #e5e7eb", background:statusFilter===f.v?f.c+"15":"#fff", color:statusFilter===f.v?f.c:"#6b7280", cursor:"pointer" }}>{f.l} ({counts[f.v]}{amounts[f.v]!==undefined?` · ${fmtCLP(amounts[f.v])}`:""})</button>
          ))}
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <select value={yearFilter} onChange={e=>setYearFilter(e.target.value)}
            style={{ padding:"8px 12px", borderRadius:8, border:"1px solid #e5e7eb", fontSize:13, fontWeight:600, color:"#374151", outline:"none", cursor:"pointer" }}>
            {availableYears.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          <input value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} placeholder="🔍 Buscar por cliente o N° factura..."
            style={{ padding:"8px 14px", borderRadius:8, border:"1px solid #e5e7eb", fontSize:13, width:260, outline:"none" }} />
        </div>
      </div>
      <div style={{ background:"#fff", borderRadius:14, overflow:"hidden", boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead><tr style={{ background:"#f8f9fb" }}>
            <SortHeader col="sii_number" label="# Factura" />
            <SortHeader col="client_name" label="Cliente" />
            <SortHeader col="issue_date" label="Fecha" />
            <SortHeader col="year_month" label="Mes de servicio" />
            <SortHeader col="net_clp" label="Neto CLP" />
            <SortHeader col="total_clp" label="Total CLP" />
            <SortHeader col="due_date" label="Vence" />
            <SortHeader col="payment_status" label="Estado" />
            <th style={{padding:"14px 16px",fontSize:12,fontWeight:600,color:"#6b7280",textTransform:"uppercase",letterSpacing:0.5,textAlign:"center"}}>Acciones</th>
          </tr></thead>
          <tbody>{sorted.map(inv=>{
            const st=sc[inv.payment_status]||sc.pending;
            const isEditing = editingId === inv.invoice_id;
            return(<tr key={inv.invoice_id} style={{borderTop:"1px solid #f3f4f6"}}>
              <td style={{padding:"14px 16px",fontWeight:600,fontFamily:"DM Mono",fontSize:13,color:inv.payment_status==="con_nc"?"#9ca3af":undefined,textDecoration:inv.payment_status==="con_nc"?"line-through":undefined}}>{inv.sii_number||"—"}</td>
              <td style={{padding:"14px 16px",fontWeight:500}}>{getName(inv.client_id)}</td>
              <td style={{padding:"14px 16px",color:"#6b7280",fontSize:13}}>
                {editingDateId === inv.invoice_id
                  ? <input type="date" value={editingDateValue} autoFocus
                      onChange={e=>setEditingDateValue(e.target.value)}
                      onBlur={()=>saveDate(inv.invoice_id, editingDateValue, inv.client_id)}
                      onKeyDown={e=>{ if(e.key==="Enter") saveDate(inv.invoice_id, editingDateValue, inv.client_id); if(e.key==="Escape") setEditingDateId(null); }}
                      style={{padding:"4px 8px",borderRadius:6,border:"1px solid #6366f1",fontSize:13,outline:"none"}} />
                  : <span onClick={()=>{setEditingDateId(inv.invoice_id);setEditingDateValue(inv.issue_date||"");}}
                      style={{cursor:"pointer",borderBottom:"1px dashed #9ca3af"}} title="Clic para editar fecha">
                      {inv.issue_date||"—"}
                    </span>
                }
              </td>
              <td style={{padding:"14px 16px",fontSize:13,color: inv.year_month ? "#374151" : "#9ca3af"}}>{fmtServiceMonth(inv.year_month)}</td>
              <td style={{padding:"14px 16px",fontFamily:"DM Mono",fontSize:13}}>${Number(inv.net_clp||0).toLocaleString("es-CL")}</td>
              <td style={{padding:"14px 16px",fontWeight:600,fontFamily:"DM Mono",fontSize:13}}>${Number(inv.total_clp||0).toLocaleString("es-CL")}</td>
              <td style={{padding:"14px 16px",color:"#6b7280",fontSize:13}}>{inv.due_date||"—"}</td>
              <td style={{padding:"14px 16px"}}>
                {isEditing ? (
                  <select value={inv.payment_status} onChange={e=>changeStatus(inv.invoice_id, e.target.value)} onBlur={()=>setEditingId(null)} autoFocus
                    style={{padding:"4px 8px",borderRadius:6,fontSize:12,border:"1px solid #e5e7eb",cursor:"pointer"}}>
                    <option value="pending">Pendiente</option>
                    <option value="paid">Pagada</option>
                    <option value="overdue">Vencida</option>
                    <option value="collection">En Cobranza</option>
                    <option value="con_nc">Con NC</option>
                  </select>
                ) : (
                  <span onClick={()=>setEditingId(inv.invoice_id)} style={{padding:"4px 10px",borderRadius:6,fontSize:12,fontWeight:600,background:st.bg,color:st.color,cursor:"pointer",border:"1px dashed transparent"}} title="Clic para cambiar estado">{st.label}</span>
                )}
              </td>
              <td style={{padding:"14px 16px",textAlign:"center"}}>
                <div style={{display:"flex",gap:4,justifyContent:"center"}}>
                  {inv.email_sent ? (
                    <span style={{fontSize:11,color:"#10b981"}} title={`Enviado: ${inv.email_sent_date||""}`}>✅ Enviado</span>
                  ) : (
                    <button onClick={async ()=>{
                      if(!window.confirm(`¿Enviar correo de factura N° ${inv.sii_number} a ${getName(inv.client_id)}?`)) return;
                      const res = await api(`/invoices/${inv.invoice_id}/send-email`, "POST", {});
                      if(res.success){showToast(`📧 Correo enviado (${res.attachments_count} adjuntos)`); loadInvoices();}
                      else showToast(res.error||"Error enviando","error");
                    }} style={{background:"#6366f1",color:"#fff",border:"none",borderRadius:6,padding:"4px 10px",fontSize:11,fontWeight:600,cursor:"pointer"}} title="Enviar factura por correo">📧</button>
                  )}
                  {inv.pdf_key && <button onClick={async()=>{const r=await api(`/invoices/${inv.invoice_id}/pdf`);if(r.success)window.open(r.url,"_blank");}} style={{background:"none",border:"1px solid #e5e7eb",borderRadius:6,padding:"4px 8px",fontSize:11,cursor:"pointer"}} title="Descargar PDF">📄</button>}
                  {inv.xml_key && <button onClick={async()=>{const r=await api(`/invoices/${inv.invoice_id}/xml`);if(r.success)downloadFromUrl(r.url);}} style={{background:"none",border:"1px solid #e5e7eb",borderRadius:6,padding:"4px 8px",fontSize:11,cursor:"pointer"}} title="Descargar XML del DTE">📋</button>}
                  {inv.sii_number && <button onClick={async()=>{const r=await api(`/invoices/${inv.invoice_id}/sobre`);if(r.success)downloadFromUrl(r.url);else showToast(r.error||"No se pudo obtener el sobre","error");}} style={{background:"none",border:"1px solid #e5e7eb",borderRadius:6,padding:"4px 8px",fontSize:11,cursor:"pointer"}} title="Descargar Sobre XML (archivo de intercambio)">📨</button>}
                </div>
              </td>
            </tr>);
          })}</tbody>
        </table>
        {sorted.length===0&&<div style={{padding:40,textAlign:"center",color:"#6b7280"}}>{invoices.length===0?"No hay facturas registradas":"No hay facturas con este filtro"}</div>}
      </div>

      {/* MODAL NOTA DE CRÉDITO */}
      {ncModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setNcModal(null)}>
          <div style={{background:"#fff",borderRadius:16,padding:32,maxWidth:480,width:"90%",boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <h3 style={{fontSize:18,fontWeight:700,color:"#1a1d29",margin:0}}>Emitir Nota de Crédito</h3>
              <button onClick={()=>setNcModal(null)} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"#6b7280"}}>✕</button>
            </div>
            <div style={{background:"#f8f9fb",borderRadius:10,padding:16,marginBottom:20}}>
              <div style={{fontSize:14,marginBottom:6}}><span style={{color:"#6b7280",fontSize:12}}>Factura N°:</span> <strong style={{fontFamily:"DM Mono"}}>{ncModal.sii_number}</strong></div>
              <div style={{fontSize:14,marginBottom:6}}><span style={{color:"#6b7280",fontSize:12}}>Cliente:</span> <strong>{getName(ncModal.client_id)}</strong></div>
              <div style={{fontSize:14}}><span style={{color:"#6b7280",fontSize:12}}>Total:</span> <strong style={{fontFamily:"DM Mono"}}>${Number(ncModal.total_clp||0).toLocaleString("es-CL")}</strong></div>
            </div>
            <div style={{background:"#fef3c7",border:"1px solid #fde68a",borderRadius:8,padding:"10px 14px",marginBottom:20,fontSize:13,color:"#92400e"}}>
              <strong>Próximamente:</strong> integración con SimpleFactura para emitir la NC directamente desde aquí con número de folio y fecha.
            </div>
            <p style={{fontSize:14,color:"#374151",marginBottom:20}}>
              Al confirmar, esta factura quedará marcada como <strong>Con NC</strong> — se excluirá de cobranza y el período volverá a quedar pendiente de facturación.
            </p>
            <div style={{display:"flex",gap:12}}>
              <button onClick={confirmNC} style={{flex:1,background:"#6b7280",color:"#fff",border:"none",borderRadius:10,padding:"12px 24px",fontSize:14,fontWeight:700,cursor:"pointer"}}>
                Confirmar NC
              </button>
              <button onClick={()=>setNcModal(null)} style={{background:"none",border:"1px solid #e5e7eb",borderRadius:10,padding:"12px 24px",fontSize:14,cursor:"pointer",color:"#6b7280"}}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TemplateEditor({ showToast }) {
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState({});
  const [selected, setSelected] = useState(null);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const loadTemplates = useCallback(async () => { const res = await api("/templates"); if (res.success) { setTemplates(res.data||[]); setVariables(res.variables||{}); } }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);
  const selectTemplate = (t) => { setSelected(t); setEditSubject(t.subject||""); setEditBody(t.body||""); setShowPreview(false); };
  const saveTemplate = async () => { if(!selected)return; setSaving(true); const res=await api(`/templates/${selected.template_id}`,"PUT",{subject:editSubject,body:editBody}); if(res.success){showToast("Plantilla guardada");loadTemplates();}else showToast(res.error||"Error","error"); setSaving(false); };
  const resetTemplate = async () => { if(!selected)return; if(!window.confirm("¿Restaurar plantilla original?"))return; const res=await api(`/templates/${selected.template_id}/reset`,"POST"); if(res.success){showToast("Restaurada");setEditSubject(res.data.subject||"");setEditBody(res.data.body||"");loadTemplates();} };
  const previewTemplate = async () => { if(!selected)return; await api(`/templates/${selected.template_id}`,"PUT",{subject:editSubject,body:editBody}); const res=await api(`/templates/${selected.template_id}/preview`,"POST"); if(res.success){setPreviewSubject(res.data.subject);setPreviewHtml(res.data.html);setShowPreview(true);} };
  const insertVariable = (v) => { const ta=document.getElementById("tpl-body"); if(ta){const s=ta.selectionStart,e=ta.selectionEnd,t=editBody;setEditBody(t.substring(0,s)+`{${v}}`+t.substring(e));setTimeout(()=>{ta.focus();ta.selectionStart=ta.selectionEnd=s+v.length+2;},0);}else setEditBody(editBody+`{${v}}`); };
  const cats = { solicitudes:{label:"📋 Solicitudes",color:"#6366f1"}, facturacion:{label:"📄 Facturación",color:"#10b981"}, cobranza:{label:"💰 Cobranza",color:"#f59e0b"}, interno:{label:"🔒 Interno",color:"#6b7280"} };
  const grouped = {}; templates.forEach(t=>{const c=t.category||"otro";if(!grouped[c])grouped[c]=[];grouped[c].push(t);});
  const IS = { width:"100%", padding:"10px 14px", borderRadius:8, border:"1px solid #e5e7eb", fontSize:14, outline:"none", background:"#fff", fontFamily:"'DM Sans', sans-serif" };
  return (
    <div>
      <h2 style={{ fontSize:24, fontWeight:700, color:"#1a1d29", margin:"0 0 8px" }}>Plantillas de Correo</h2>
      <p style={{ color:"#6b7280", margin:"0 0 24px", fontSize:14 }}>Edita los correos automáticos. Usa variables como {"{nombre_cliente}"} para datos dinámicos.</p>
      <div style={{ display:"grid", gridTemplateColumns:"300px 1fr", gap:24 }}>
        <div style={{ background:"#fff", borderRadius:14, padding:20, boxShadow:"0 1px 3px rgba(0,0,0,0.06)", alignSelf:"start" }}>
          {Object.entries(grouped).map(([cat,items])=>{const ci=cats[cat]||{label:cat,color:"#6b7280"};return(<div key={cat} style={{marginBottom:20}}><p style={{fontSize:12,fontWeight:700,color:ci.color,textTransform:"uppercase",letterSpacing:0.5,margin:"0 0 8px"}}>{ci.label}</p>{items.map(t=>(<button key={t.template_id} onClick={()=>selectTemplate(t)} style={{display:"block",width:"100%",textAlign:"left",padding:"10px 12px",borderRadius:8,border:"none",cursor:"pointer",marginBottom:4,background:selected?.template_id===t.template_id?"#e0e7ff":"transparent",color:selected?.template_id===t.template_id?"#4338ca":"#374151",fontWeight:selected?.template_id===t.template_id?600:400,fontSize:13,fontFamily:"'DM Sans', sans-serif"}}>{t.name}</button>))}</div>);})}
        </div>
        {selected ? (<div>
          <div style={{ background:"#fff", borderRadius:14, padding:28, boxShadow:"0 1px 3px rgba(0,0,0,0.06)", marginBottom:20 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
              <h3 style={{ fontSize:18, fontWeight:600, color:"#1a1d29", margin:0 }}>{selected.name}</h3>
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={previewTemplate} style={{background:"none",border:"1px solid #e5e7eb",borderRadius:8,padding:"6px 14px",fontSize:13,cursor:"pointer",color:"#6b7280"}}>👁️ Vista previa</button>
                <button onClick={resetTemplate} style={{background:"none",border:"1px solid #e5e7eb",borderRadius:8,padding:"6px 14px",fontSize:13,cursor:"pointer",color:"#6b7280"}}>↩️ Restaurar</button>
                <button onClick={saveTemplate} disabled={saving} style={{background:"#6366f1",color:"#fff",border:"none",borderRadius:8,padding:"6px 18px",fontSize:13,fontWeight:600,cursor:"pointer",opacity:saving?0.6:1}}>{saving?"Guardando...":"💾 Guardar"}</button>
              </div>
            </div>
            <p style={{ color:"#9ca3af", fontSize:13, margin:"4px 0 20px" }}>{selected.description}</p>
            <div style={{ marginBottom:16 }}><label style={{display:"block",fontSize:13,fontWeight:600,color:"#374151",marginBottom:6}}>Asunto</label><input value={editSubject} onChange={e=>setEditSubject(e.target.value)} style={IS} /></div>
            <div style={{ marginBottom:16 }}><label style={{display:"block",fontSize:13,fontWeight:600,color:"#374151",marginBottom:6}}>Cuerpo</label><textarea id="tpl-body" value={editBody} onChange={e=>setEditBody(e.target.value)} rows={12} style={{...IS,resize:"vertical",lineHeight:1.6,fontFamily:"'DM Mono', monospace",fontSize:13}} /></div>
            <div><label style={{display:"block",fontSize:13,fontWeight:600,color:"#374151",marginBottom:8}}>Variables <span style={{fontWeight:400,color:"#9ca3af"}}>(clic para insertar)</span></label>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>{(selected.variables||[]).map(v=>(<button key={v} onClick={()=>insertVariable(v)} style={{padding:"4px 10px",borderRadius:6,fontSize:12,background:"#f0f4ff",color:"#4338ca",border:"1px solid #c7d2fe",cursor:"pointer",fontFamily:"'DM Mono', monospace"}}>{"{"+v+"}"}</button>))}</div>
              <div style={{marginTop:12,padding:12,background:"#f8f9fb",borderRadius:8}}>{(selected.variables||[]).map(v=>(<div key={v} style={{fontSize:12,color:"#6b7280",marginBottom:4}}><code style={{color:"#4338ca",fontFamily:"'DM Mono', monospace"}}>{"{"+v+"}"}</code>{" → "}{variables[v]||""}</div>))}</div>
            </div>
          </div>
          {showPreview && (<div style={{background:"#fff",borderRadius:14,padding:28,boxShadow:"0 1px 3px rgba(0,0,0,0.06)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}><h3 style={{fontSize:16,fontWeight:600,color:"#1a1d29",margin:0}}>👁️ Vista Previa</h3><button onClick={()=>setShowPreview(false)} style={{background:"none",border:"1px solid #e5e7eb",borderRadius:6,padding:"4px 10px",fontSize:12,cursor:"pointer",color:"#6b7280"}}>Cerrar</button></div>
            <div style={{padding:"8px 12px",background:"#f8f9fb",borderRadius:8,marginBottom:16}}><span style={{fontSize:12,color:"#6b7280"}}>Asunto: </span><span style={{fontSize:14,fontWeight:600,color:"#1a1d29"}}>{previewSubject}</span></div>
            <div style={{border:"1px solid #e5e7eb",borderRadius:8,overflow:"hidden"}}><iframe title="preview" srcDoc={previewHtml} style={{width:"100%",height:500,border:"none"}} /></div>
            <p style={{fontSize:12,color:"#9ca3af",marginTop:8}}>ℹ️ Vista previa con datos de ejemplo.</p>
          </div>)}
        </div>) : (<div style={{background:"#fff",borderRadius:14,padding:60,textAlign:"center",boxShadow:"0 1px 3px rgba(0,0,0,0.06)"}}><p style={{fontSize:36,margin:"0 0 12px"}}>✉️</p><p style={{color:"#6b7280",fontSize:15}}>Selecciona una plantilla para editarla</p></div>)}
      </div>
    </div>
  );
}

function Conciliation({ showToast, onRefresh }) {
  const [suggestions, setSuggestions] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState(null);
  const [confirming, setConfirming] = useState(null);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const token = sessionStorage.getItem("egenya_token");
      const res = await fetch(`${API_URL}/conciliation/upload`, { method: "POST", body: formData, headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) {
        setSuggestions(data.data.suggestions);
        setStats({ total: data.data.total_movements, matched: data.data.total_matched, unmatched: data.data.total_unmatched });
        showToast(`${data.data.total_movements} ingresos encontrados, ${data.data.total_matched} coincidencias`);
      } else { showToast(data.error || "Error", "error"); }
    } catch (err) { showToast("Error al procesar: " + err.message, "error"); }
    setUploading(false);
    e.target.value = "";
  };

  const confirmMatch = async (suggestion) => {
    const invoiceIds = suggestion.invoices.map(i => i.invoice_id);
    setConfirming(invoiceIds.join(","));
    const res = await api("/conciliation/confirm", "POST", {
      invoice_ids: invoiceIds,
      payment_date: suggestion.movement.date,
      payment_amount: suggestion.movement.amount,
      movement: suggestion.movement,
    });
    if (res.success) {
      const nums = suggestion.invoices.map(i => `#${i.sii_number}`).join(", ");
      showToast(`✅ ${suggestion.invoices.length > 1 ? "Facturas" : "Factura"} ${nums} marcada${suggestion.invoices.length > 1 ? "s" : ""} como pagada${suggestion.invoices.length > 1 ? "s" : ""}`);
      setSuggestions(prev => prev.map(s => {
        if (s.invoices && s.invoices.some(inv => invoiceIds.includes(inv.invoice_id))) return { ...s, confirmed: true };
        return s;
      }));
      if (onRefresh) onRefresh();
    } else { showToast(res.error, "error"); }
    setConfirming(null);
  };

  const matched = suggestions?.filter(s => s.invoices && !s.confirmed) || [];
  const confirmed = suggestions?.filter(s => s.confirmed) || [];
  const unmatched = suggestions?.filter(s => !s.invoices) || [];

  return (
    <div>
      <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a1d29", margin: "0 0 8px" }}>🏦 Conciliación Bancaria</h2>
      <p style={{ color: "#6b7280", margin: "0 0 24px", fontSize: 14 }}>Sube la cartola BCI en Excel y el sistema cruzará automáticamente los pagos con las facturas pendientes.</p>

      <div style={{ background: "#fff", borderRadius: 14, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 24px", borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: uploading ? "wait" : "pointer", background: "#6366f1", color: "#fff", border: "none" }}>
            {uploading ? "Procesando..." : "📤 Subir Cartola BCI (Excel)"}
            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleUpload} style={{ display: "none" }} disabled={uploading} />
          </label>
          {stats && (
            <div style={{ display: "flex", gap: 16, fontSize: 14 }}>
              <span style={{ color: "#6b7280" }}>Ingresos: <strong>{stats.total}</strong></span>
              <span style={{ color: "#10b981" }}>Coincidencias: <strong>{stats.matched}</strong></span>
              <span style={{ color: "#f59e0b" }}>Sin match: <strong>{stats.unmatched}</strong></span>
            </div>
          )}
        </div>
      </div>

      {!suggestions && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 60, textAlign: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
          <p style={{ fontSize: 48, margin: "0 0 12px" }}>🏦</p>
          <p style={{ color: "#6b7280", fontSize: 15 }}>Descarga la cartola desde el portal BCI en formato Excel y súbela aquí</p>
          <p style={{ color: "#9ca3af", fontSize: 13, marginTop: 8 }}>El sistema cruzará automáticamente los ingresos con tus facturas pendientes</p>
        </div>
      )}

      {/* Confirmados */}
      {confirmed.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: "#10b981", margin: "0 0 12px" }}>✅ Confirmados ({confirmed.length})</h3>
          {confirmed.map((s, i) => (
            <div key={i} style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: 14, marginBottom: 8, opacity: 0.7 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <span style={{ fontWeight: 600, color: "#065f46" }}>{s.invoices.map(inv => inv.client_name).filter((v,i,a)=>a.indexOf(v)===i).join(", ")}</span>
                  <span style={{ marginLeft: 12, fontSize: 13, color: "#6b7280" }}>
                    {s.invoices.map(inv => `#${inv.sii_number}`).join(", ")}
                  </span>
                </div>
                <span style={{ fontWeight: 700, color: "#065f46", fontFamily: "DM Mono" }}>${s.movement.amount.toLocaleString("es-CL")}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Con match - para confirmar */}
      {matched.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: "#6366f1", margin: "0 0 12px" }}>🔗 Coincidencias ({matched.length})</h3>
          {matched.sort((a,b)=>b.match_score-a.match_score).map((s, i) => (
            <div key={i} style={{ background: "#fff", border: "1px solid #e0e7ff", borderRadius: 10, padding: 16, marginBottom: 10, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700, background: "#d1fae5", color: "#065f46" }}>{s.match_score}%</span>
                    <span style={{ fontSize: 12, color: "#6b7280" }}>{s.match_reasons.join(" · ")}</span>
                    {s.invoices.length > 1 && <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700, background: "#ede9fe", color: "#5b21b6" }}>Pago múltiple ({s.invoices.length} facturas)</span>}
                  </div>
                  {s.warning && <div style={{ fontSize: 12, color: "#92400e", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 6, padding: "5px 10px", marginBottom: 6 }}>{s.warning}</div>}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div style={{ background: "#f8f9fb", borderRadius: 8, padding: 12 }}>
                      <p style={{ fontSize: 11, color: "#6b7280", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: 0.5 }}>Movimiento BCI</p>
                      <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1d29", margin: 0 }}>{s.movement.description}</p>
                      <p style={{ fontSize: 13, color: "#6b7280", margin: "4px 0 0" }}>{s.movement.date}{s.movement.rut && ` · RUT: ${s.movement.rut}`}</p>
                      <p style={{ fontSize: 16, fontWeight: 700, color: "#10b981", margin: "4px 0 0", fontFamily: "DM Mono" }}>${s.movement.amount.toLocaleString("es-CL")}</p>
                    </div>
                    <div style={{ background: "#f0f4ff", borderRadius: 8, padding: 12 }}>
                      <p style={{ fontSize: 11, color: "#6b7280", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: 0.5 }}>{s.invoices.length > 1 ? "Facturas" : "Factura"}</p>
                      {s.invoices.map((inv, j) => (
                        <div key={j} style={{ marginBottom: j < s.invoices.length - 1 ? 8 : 0, paddingBottom: j < s.invoices.length - 1 ? 8 : 0, borderBottom: j < s.invoices.length - 1 ? "1px dashed #c7d2fe" : "none" }}>
                          <p style={{ fontSize: 14, fontWeight: 600, color: "#1a1d29", margin: 0 }}>{inv.client_name}</p>
                          <p style={{ fontSize: 13, color: "#6b7280", margin: "2px 0 0" }}>#{inv.sii_number} · Emitida: {inv.issue_date}</p>
                          <p style={{ fontSize: 15, fontWeight: 700, color: "#6366f1", margin: "2px 0 0", fontFamily: "DM Mono" }}>${inv.total_clp.toLocaleString("es-CL")}</p>
                        </div>
                      ))}
                      {s.invoices.length > 1 && (
                        <div style={{ marginTop: 6, paddingTop: 6, borderTop: "2px solid #a5b4fc" }}>
                          <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>Suma total:</p>
                          <p style={{ fontSize: 16, fontWeight: 700, color: "#6366f1", margin: 0, fontFamily: "DM Mono" }}>${s.invoices.reduce((sum, inv) => sum + inv.total_clp, 0).toLocaleString("es-CL")}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <button onClick={() => confirmMatch(s)} disabled={!!confirming}
                  style={{ marginLeft: 12, background: "#10b981", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", opacity: confirming ? 0.6 : 1 }}>
                  {confirming ? "..." : "✅ Confirmar"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sin match */}
      {unmatched.length > 0 && (
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: "#f59e0b", margin: "0 0 12px" }}>❓ Sin Coincidencia ({unmatched.length})</h3>
          {unmatched.map((s, i) => (
            <div key={i} style={{ background: "#fff", border: "1px solid #fef3c7", borderRadius: 10, padding: 14, marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <span style={{ fontWeight: 500, color: "#1a1d29" }}>{s.movement.description}</span>
                  <span style={{ marginLeft: 12, fontSize: 12, color: "#6b7280" }}>{s.movement.date}{s.movement.rut && ` · ${s.movement.rut}`}</span>
                  {s.movement.comment && <span style={{ marginLeft: 8, fontSize: 12, color: "#9ca3af" }}>"{s.movement.comment}"</span>}
                </div>
                <span style={{ fontWeight: 600, color: "#10b981", fontFamily: "DM Mono" }}>${s.movement.amount.toLocaleString("es-CL")}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SystemSettings({ showToast }) {
  const [emails, setEmails] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api("/system-config", "GET").then(res => {
      if (res.success) setEmails((res.data.internal_team_emails || []).join(", "));
      setLoading(false);
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const res = await api("/system-config", "PUT", { internal_team_emails: emails });
    if (res.success) showToast("Configuración guardada");
    else showToast(res.error || "Error al guardar", "error");
    setSaving(false);
  };

  const L = { display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 };
  const S = { width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14, outline: "none" };

  return (
    <div style={{ maxWidth: 640 }}>
      <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a1d29", margin: "0 0 8px" }}>Configuración</h2>
      <p style={{ color: "#6b7280", margin: "0 0 32px", fontSize: 14 }}>Ajustes generales del sistema de facturación.</p>

      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", padding: 24 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: "#6366f1", margin: "0 0 4px", paddingBottom: 8, borderBottom: "2px solid #e0e7ff" }}>Equipo Interno</h3>
        <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 20px" }}>Estos correos reciben el resumen diario, alertas de cobranza, documentos faltantes y copia (CC) de todos los envíos automáticos. Para agregar más de uno, sepáralos con coma.</p>
        {loading ? (
          <p style={{ color: "#9ca3af", fontSize: 14 }}>Cargando...</p>
        ) : (
          <div>
            <label style={L}>Correos notificaciones internas</label>
            <input
              value={emails}
              onChange={e => setEmails(e.target.value)}
              placeholder="correo1@egenya.cl, correo2@egenya.cl"
              style={S}
            />
            <p style={{ fontSize: 12, color: "#9ca3af", margin: "6px 0 20px" }}>Actualmente: {emails || "—"}</p>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{ background: "#6366f1", color: "#fff", border: "none", borderRadius: 8, padding: "10px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
            >
              {saving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const CHART_COLORS = ["#6366f1","#10b981","#f59e0b","#ef4444","#3b82f6","#ec4899","#8b5cf6","#14b8a6","#f97316","#84cc16"];
const MONTHS_SHORT = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const STATUS_COLORS = { pending:"#f59e0b", paid:"#10b981", overdue:"#ef4444", collection:"#ec4899", con_nc:"#6b7280" };
const STATUS_LABELS = { pending:"Pendiente", paid:"Pagada", overdue:"Vencida", collection:"Cobranza", con_nc:"Con NC" };
const VERTICAL_COLORS = { "Salud":"#6366f1", "Educación":"#10b981", "Productivo":"#f59e0b", "Activo inmobiliario":"#8b5cf6", "Prestador":"#ef4444" };

function fmtCLP(v) {
  if (v >= 1000000) return `$${(v/1000000).toFixed(1)}M`;
  if (v >= 1000) return `$${(v/1000).toFixed(0)}k`;
  return `$${v}`;
}

function SalesPanel({ invoices, clients }) {
  const clientMap = {};
  clients.forEach(c => { clientMap[c.client_id] = c.client_name; });

  // --- Clientes activos por RUT ---
  const activeClients = clients.filter(c => c.status === "active");
  const totalByName = activeClients.length;
  const rutMap = {};
  activeClients.forEach(c => {
    const rut = c.rut || "Sin RUT";
    if (!rutMap[rut]) rutMap[rut] = [];
    rutMap[rut].push(c.client_name);
  });
  const totalByRut = Object.keys(rutMap).length;
  const rutChartData = Object.entries(rutMap)
    .sort((a, b) => b[1].length - a[1].length)
    .map(([rut, names]) => ({ rut, cantidad: names.length, clientes: names.join(", ") }));
  const multiRut = rutChartData.filter(r => r.cantidad > 1);

  // Años disponibles
  // Excluir facturas "Con NC" (anuladas por nota de crédito): no representan venta real
  const allValid = invoices.filter(i => i.net_clp && i.issue_date && i.payment_status !== "con_nc");
  const availableYears = [...new Set(allValid.map(i => i.issue_date.slice(0,4)))].sort();
  const [yearFilter, setYearFilter] = useState("all");

  // Solo facturas con monto filtradas por año
  const valid = yearFilter === "all" ? allValid : allValid.filter(i => i.issue_date.slice(0,4) === yearFilter);

  // --- 1. Ingresos por mes (barras) — nacional vs exportación ---
  const byMonth = {};
  valid.forEach(i => {
    const ym = i.issue_date.slice(0, 7);
    if (!byMonth[ym]) byMonth[ym] = { nacional: 0, exportacion: 0 };
    const bucket = i.invoice_class === "exportacion" ? "exportacion" : "nacional";
    byMonth[ym][bucket] += Number(i.net_clp);
  });
  const monthData = Object.keys(byMonth).sort().map(ym => {
    const [y, m] = ym.split("-");
    const { nacional, exportacion } = byMonth[ym];
    return { mes: `${MONTHS_SHORT[parseInt(m,10)-1]} ${y.slice(2)}`, nacional, exportacion, neto: nacional + exportacion, ym };
  });

  // --- 2. Top 10 clientes ---
  const byClient = {};
  valid.forEach(i => {
    const name = clientMap[i.client_id] || i.client_name || i.client_id;
    byClient[name] = (byClient[name] || 0) + Number(i.net_clp);
  });
  const topClients = Object.entries(byClient)
    .sort((a,b) => b[1]-a[1]).slice(0,10)
    .map(([name, neto]) => ({ name, neto }));

  // --- 3. Cartera por estado (dona) ---
  const byStatus = {};
  valid.forEach(i => {
    const s = i.payment_status || "pending";
    byStatus[s] = (byStatus[s] || 0) + Number(i.total_clp || i.net_clp);
  });
  const statusData = Object.entries(byStatus).map(([s, v]) => ({ name: STATUS_LABELS[s]||s, value: v, status: s }));

  // --- 4. Tendencia acumulada ---
  let acum = 0;
  const trendData = monthData.map(d => { acum += d.neto; return { ...d, acumulado: acum }; });

  // --- 5. Facturación por Vertical ---
  const clientVerticalMap = {};
  clients.forEach(c => { clientVerticalMap[c.client_id] = c.vertical || "Sin vertical"; });
  const byVertical = {};
  valid.forEach(i => {
    const v = clientVerticalMap[i.client_id] || "Sin vertical";
    byVertical[v] = (byVertical[v] || 0) + Number(i.net_clp);
  });
  const verticalData = VERTICALS.map(v => ({ name: v, neto: byVertical[v] || 0 }));
  const totalNetoVertical = verticalData.reduce((s, d) => s + d.neto, 0);

  const cardStyle = { background:"#fff", borderRadius:14, padding:24, boxShadow:"0 1px 3px rgba(0,0,0,0.06)", marginBottom:24 };
  const titleStyle = { fontSize:15, fontWeight:700, color:"#1a1d29", margin:"0 0 20px" };

  const CustomTooltipCLP = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:8, padding:"10px 14px", fontSize:13 }}>
        <div style={{ fontWeight:600, marginBottom:4 }}>{label}</div>
        {payload.map((p,i) => (
          <div key={i} style={{ color:p.color }}>{p.name}: ${Number(p.value).toLocaleString("es-CL")}</div>
        ))}
      </div>
    );
  };

  const totalNeto = valid.reduce((s,i) => s + Number(i.net_clp), 0);
  const totalFacturas = valid.length;

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
        <h2 style={{ fontSize:24, fontWeight:700, color:"#1a1d29", margin:0 }}>Panel de Ventas</h2>
        <div style={{ display:"flex", gap:8 }}>
          {["all", ...availableYears].map(y => (
            <button key={y} onClick={() => setYearFilter(y)}
              style={{ padding:"7px 16px", borderRadius:8, fontSize:13, fontWeight:yearFilter===y?700:400, border:yearFilter===y?"2px solid #6366f1":"1px solid #e5e7eb", background:yearFilter===y?"#eef2ff":"#fff", color:yearFilter===y?"#6366f1":"#6b7280", cursor:"pointer" }}>
              {y === "all" ? "Histórico" : y}
            </button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16, marginBottom:24 }}>
        {[
          { label:"Total Facturado (Neto)", value:`$${totalNeto.toLocaleString("es-CL")}`, color:"#6366f1" },
          { label:"Total Facturas", value:totalFacturas, color:"#10b981" },
          { label:"Promedio Mensual", value: monthData.length ? `$${Math.round(totalNeto/monthData.length).toLocaleString("es-CL")}` : "—", color:"#f59e0b" },
        ].map((k,i) => (
          <div key={i} style={{ background:"#fff", borderRadius:14, padding:20, boxShadow:"0 1px 3px rgba(0,0,0,0.06)", borderTop:`4px solid ${k.color}` }}>
            <div style={{ fontSize:12, color:"#6b7280", fontWeight:500, marginBottom:8 }}>{k.label}</div>
            <div style={{ fontSize:24, fontWeight:700, color:k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Clientes activos: por nombre vs por RUT */}
      <div style={cardStyle}>
        <h3 style={titleStyle}>Clientes activos</h3>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:24 }}>
          {/* Izquierda: dos KPIs + gráfico */}
          <div>
            <div style={{ display:"flex", gap:16, marginBottom:20 }}>
              <div style={{ flex:1, background:"#eef2ff", borderRadius:12, padding:"16px 20px", textAlign:"center" }}>
                <div style={{ fontSize:36, fontWeight:800, color:"#6366f1" }}>{totalByName}</div>
                <div style={{ fontSize:12, color:"#6b7280", fontWeight:500, marginTop:4 }}>Cuentas activas</div>
              </div>
              <div style={{ flex:1, background:"#f0fdf4", borderRadius:12, padding:"16px 20px", textAlign:"center" }}>
                <div style={{ fontSize:36, fontWeight:800, color:"#10b981" }}>{totalByRut}</div>
                <div style={{ fontSize:12, color:"#6b7280", fontWeight:500, marginTop:4 }}>RUTs únicos</div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={[
                { label:"Cuentas activas", valor: totalByName, fill:"#6366f1" },
                { label:"RUTs únicos", valor: totalByRut, fill:"#10b981" },
              ]} margin={{ top:5, right:10, left:0, bottom:5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize:12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize:12 }} />
                <Tooltip formatter={(v) => v} />
                <Bar dataKey="valor" name="Cantidad" radius={[6,6,0,0]}>
                  {[{fill:"#6366f1"},{fill:"#10b981"}].map((d,i) => <Cell key={i} fill={d.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Derecha: RUTs con múltiples cuentas */}
          <div>
            <div style={{ fontSize:13, fontWeight:600, color:"#374151", marginBottom:12 }}>
              RUTs con múltiples cuentas ({multiRut.length})
            </div>
            {multiRut.length === 0 ? (
              <p style={{ fontSize:13, color:"#9ca3af" }}>Todos los RUTs tienen una sola cuenta.</p>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {multiRut.map((r, i) => (
                  <div key={i} style={{ background:"#f9fafb", borderRadius:8, padding:"10px 14px", border:"1px solid #e5e7eb" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                      <span style={{ fontSize:13, fontWeight:700, color:"#1a1d29", fontFamily:"monospace" }}>{r.rut}</span>
                      <span style={{ fontSize:12, fontWeight:700, background:"#6366f1", color:"#fff", borderRadius:20, padding:"2px 10px" }}>{r.cantidad} cuentas</span>
                    </div>
                    <div style={{ fontSize:12, color:"#6b7280" }}>{r.clientes}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Facturación por Vertical */}
      <div style={cardStyle}>
        <h3 style={titleStyle}>Facturación por Vertical (Neto CLP)</h3>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12, marginBottom:24 }}>
          {verticalData.map(vd => (
            <div key={vd.name} style={{ background:"#f9fafb", borderRadius:12, padding:"16px 14px", textAlign:"center", borderTop:`4px solid ${VERTICAL_COLORS[vd.name]||"#6b7280"}` }}>
              <div style={{ fontSize:11, color:"#6b7280", fontWeight:600, marginBottom:6, lineHeight:1.3 }}>{vd.name}</div>
              <div style={{ fontSize:17, fontWeight:700, color:VERTICAL_COLORS[vd.name]||"#1a1d29" }}>${Math.round(vd.neto).toLocaleString("es-CL")}</div>
              <div style={{ fontSize:11, color:"#9ca3af", marginTop:4 }}>{totalNetoVertical > 0 ? `${((vd.neto/totalNetoVertical)*100).toFixed(1)}%` : "—"}</div>
            </div>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={verticalData} margin={{ top:5, right:20, left:10, bottom:5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fontSize:12 }} />
            <YAxis tickFormatter={fmtCLP} tick={{ fontSize:12 }} />
            <Tooltip content={<CustomTooltipCLP />} />
            <Bar dataKey="neto" name="Neto CLP" radius={[4,4,0,0]}>
              {verticalData.map((d, i) => <Cell key={i} fill={VERTICAL_COLORS[d.name] || CHART_COLORS[i]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Ingresos por mes */}
      <div style={cardStyle}>
        <h3 style={titleStyle}>Ingresos por mes (Neto CLP)</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={monthData} margin={{ top:24, right:20, left:10, bottom:5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="mes" tick={{ fontSize:12 }} />
            <YAxis tickFormatter={fmtCLP} tick={{ fontSize:12 }} />
            <Tooltip content={<CustomTooltipCLP />} />
            <Legend wrapperStyle={{ fontSize:12 }} />
            <Bar dataKey="nacional" name="Nacional" stackId="a" fill="#3b82f6">
              <LabelList dataKey="nacional" position="center"
                formatter={(v) => (v > 0 ? fmtCLP(v) : "")}
                style={{ fill:"#fff", fontSize:11, fontWeight:600 }} />
            </Bar>
            <Bar dataKey="exportacion" name="Exportación" stackId="a" fill="#f59e0b" radius={[4,4,0,0]}>
              <LabelList dataKey="exportacion" position="center"
                formatter={(v) => (v > 0 ? fmtCLP(v) : "")}
                style={{ fill:"#fff", fontSize:11, fontWeight:600 }} />
              <LabelList dataKey="neto" position="top"
                formatter={(v) => (v > 0 ? fmtCLP(v) : "")}
                style={{ fill:"#1a1d29", fontSize:11, fontWeight:700 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Tendencia acumulada */}
      <div style={cardStyle}>
        <h3 style={titleStyle}>Tendencia acumulada de ventas</h3>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={trendData} margin={{ top:5, right:20, left:10, bottom:5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="mes" tick={{ fontSize:12 }} />
            <YAxis tickFormatter={fmtCLP} tick={{ fontSize:12 }} />
            <Tooltip content={<CustomTooltipCLP />} />
            <Line type="monotone" dataKey="acumulado" name="Acumulado CLP" stroke="#10b981" strokeWidth={2.5} dot={{ r:4, fill:"#10b981" }} activeDot={{ r:6 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:24 }}>
        {/* Top clientes */}
        <div style={cardStyle}>
          <h3 style={titleStyle}>Top 10 clientes por facturación</h3>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={topClients} layout="vertical" margin={{ top:0, right:20, left:100, bottom:0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tickFormatter={fmtCLP} tick={{ fontSize:11 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize:11 }} width={95} />
              <Tooltip content={<CustomTooltipCLP />} />
              <Bar dataKey="neto" name="Neto CLP" radius={[0,4,4,0]}>
                {topClients.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Cartera por estado */}
        <div style={cardStyle}>
          <h3 style={titleStyle}>Estado de cartera (Total CLP)</h3>
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie data={statusData} cx="50%" cy="45%" innerRadius={70} outerRadius={110}
                dataKey="value" nameKey="name" paddingAngle={3}
                label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}
                labelLine={false}>
                {statusData.map((d, i) => <Cell key={i} fill={STATUS_COLORS[d.status] || CHART_COLORS[i]} />)}
              </Pie>
              <Tooltip formatter={(v) => `$${Number(v).toLocaleString("es-CL")}`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// COMUNICACIONES
// ============================================================
function CommunicationsLog() {
  const now = new Date();
  const defaultYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [yearMonth, setYearMonth] = useState(defaultYm);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState("all");

  const load = useCallback(async (ym) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/communications/log?year_month=${ym}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("egenya_token")}` },
      });
      const data = await res.json();
      if (data.success) setEvents(data.events || []);
      else setError(data.error || "Error al cargar");
    } catch (e) {
      setError("Error de conexión");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(yearMonth); }, [yearMonth, load]);

  const TYPE_CONFIG = {
    oc_request:          { label: "Solicitud OC",       bg: "#dbeafe", color: "#1d4ed8", dot: "#3b82f6" },
    hes_request:         { label: "Solicitud HES/NdP",  bg: "#ffedd5", color: "#c2410c", dot: "#f97316" },
    cobranza_recordatorio:   { label: "Recordatorio",   bg: "#fef9c3", color: "#854d0e", dot: "#eab308" },
    cobranza_alerta_interna: { label: "Alerta interna", bg: "#ffedd5", color: "#9a3412", dot: "#f97316" },
    cobranza_ultimo_aviso:   { label: "Último aviso",   bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" },
  };

  const typeGroups = [
    { key: "all", label: "Todas" },
    { key: "oc_request", label: "OC" },
    { key: "hes_request", label: "HES/NdP" },
    { key: "cobranza", label: "Cobranza" },
  ];

  const filtered = filterType === "all" ? events
    : filterType === "cobranza" ? events.filter(e => e.type.startsWith("cobranza"))
    : events.filter(e => e.type === filterType);

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const prevMonth = () => {
    const [y, m] = yearMonth.split("-").map(Number);
    const d = new Date(y, m - 2, 1);
    setYearMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  const nextMonth = () => {
    const [y, m] = yearMonth.split("-").map(Number);
    const d = new Date(y, m, 1);
    setYearMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const monthLabel = (() => {
    const [y, m] = yearMonth.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString("es-CL", { month: "long", year: "numeric" });
  })();

  const countByType = {
    oc_request: events.filter(e => e.type === "oc_request").length,
    hes_request: events.filter(e => e.type === "hes_request").length,
    cobranza: events.filter(e => e.type.startsWith("cobranza")).length,
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a1d29", margin: 0 }}>📨 Comunicaciones</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={prevMonth} style={{ border: "1px solid #e5e7eb", background: "#fff", borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 14 }}>‹</button>
          <span style={{ fontWeight: 600, fontSize: 15, minWidth: 160, textAlign: "center", textTransform: "capitalize" }}>{monthLabel}</span>
          <button onClick={nextMonth} disabled={yearMonth >= defaultYm} style={{ border: "1px solid #e5e7eb", background: "#fff", borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 14, opacity: yearMonth >= defaultYm ? 0.4 : 1 }}>›</button>
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { key: "oc_request",  label: "Solicitudes OC",      icon: "📋", color: "#3b82f6" },
          { key: "hes_request", label: "Solicitudes HES/NdP", icon: "📄", color: "#f97316" },
          { key: "cobranza",    label: "Acciones cobranza",   icon: "💰", color: "#ef4444" },
        ].map(k => (
          <div key={k.key} onClick={() => setFilterType(filterType === k.key ? "all" : k.key)}
            style={{ background: filterType === k.key ? k.color : "#fff", color: filterType === k.key ? "#fff" : "#1a1d29",
              border: `2px solid ${filterType === k.key ? k.color : "#e5e7eb"}`, borderRadius: 12, padding: "16px 20px",
              cursor: "pointer", transition: "all .15s" }}>
            <div style={{ fontSize: 22, marginBottom: 4 }}>{k.icon}</div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{countByType[k.key]}</div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {typeGroups.map(g => (
          <button key={g.key} onClick={() => setFilterType(g.key)}
            style={{ padding: "6px 14px", borderRadius: 20, border: "none", cursor: "pointer", fontSize: 13,
              background: filterType === g.key ? "#6366f1" : "#f3f4f6",
              color: filterType === g.key ? "#fff" : "#374151", fontWeight: filterType === g.key ? 600 : 400 }}>
            {g.label}
          </button>
        ))}
        <span style={{ marginLeft: "auto", fontSize: 13, color: "#6b7280", alignSelf: "center" }}>
          {loading ? "Cargando..." : `${filtered.length} evento${filtered.length !== 1 ? "s" : ""}`}
        </span>
      </div>

      {error && <div style={{ background: "#fee2e2", color: "#991b1b", padding: "10px 16px", borderRadius: 8, marginBottom: 16, fontSize: 13 }}>{error}</div>}

      {/* Tabla */}
      {filtered.length === 0 && !loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
          <div style={{ fontSize: 15 }}>No hay comunicaciones en este período</div>
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                {["Fecha", "Tipo", "Cliente", "Destinatario", "Detalle"].map(h => (
                  <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "#374151", fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((ev, i) => {
                const cfg = TYPE_CONFIG[ev.type] || { label: ev.label, bg: "#f3f4f6", color: "#374151", dot: "#9ca3af" };
                return (
                  <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "10px 14px", color: "#6b7280", whiteSpace: "nowrap" }}>{formatDate(ev.date)}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: cfg.bg, color: cfg.color,
                        borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.dot, display: "inline-block" }} />
                        {cfg.label || ev.label}
                      </span>
                    </td>
                    <td style={{ padding: "10px 14px", fontWeight: 500 }}>{ev.client_name}</td>
                    <td style={{ padding: "10px 14px", color: "#6b7280" }}>
                      <div>{ev.recipient || "—"}</div>
                      {ev.recipient_name && <div style={{ fontSize: 11, color: "#9ca3af" }}>{ev.recipient_name}</div>}
                    </td>
                    <td style={{ padding: "10px 14px", color: "#6b7280" }}>{ev.detail}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default App;
